import { useState, useReducer, useEffect, useRef, Fragment, useCallback, useMemo } from 'react';
import { bannerAdPositions, bidderStatus, contactType, eventCategory, inquiryType, gtmEventType, webSavedSearchAuthenticatedLimit, rentalFacetOptions, leaseFacetOptions, cookieNames, defaultListPageSize, defaultNewListPageSize, secondsLeftToAddListingToCountdown, queryString } from '@Components/Utilities/SandhillsConstants';
import Paging from '@Components/MuiWrappers/Paging';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
//import { onINP } from 'web-vitals'; //commenting out import until needed again
import { watchlistOnClick } from '@Components/Utilities/SandhillsWatchlist';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import ComplexBreadcrumb from '@Components/Shared/ComplexBreadcrumb';
import DoubleClickAd from '@Components/Shared/DoubleClick/DoubleClickAd';
import listingsReducer from '@Components/Listings/ListingsReducer';
import { setBetaSiteCookie, clearCookie, getCookie, tryAddListingToCompareCookie, tryDeleteCompareListingFromCookie } from '@Components/Utilities/CookieHelper';
import ready from 'document-ready';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import { togglePushNotifications } from '@Components/Shared/PushNotificationButton';
import debounce from 'lodash/debounce';
import { toggleScrollToTop } from '@Components/Shared/ScrollToTop';
import ShowClosestFirstToggle from '@Components/Shared/ShowClosestFirstToggle';
import { runSortWithCurrentLocation_CookiesFirst } from '@Components/Utilities/DistanceSortHelper';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import CategoryInformation from '@Components/Shared/CategoryInformation';
import WebSavedSearchesButton from '@Components/WebSavedSearches/WebSavedSearchesButton';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import ListingControlBar from '@Components/Listings/ListingControlBar';
import AdditionalPageInformation from '@Components/Listings/AdditionalPageInformation';
import ListingOptions from '@Components/Shared/ListingOptions';
import WebListing_GridCard from '@Components/Listings/WebListing_GridCard';
import WebListing_MobileCard from '@Components/Listings/WebListing_MobileCard';
import axios from 'axios';
import QuickFind from '@Components/Header/QuickFind';
import WizardBanner from '@Components/Wizard/WizardBanner';

import {
    setWindowOverrides,
    sandhillsRequest,
    repositionCookieBanner,
    getUserSettingsSelectorCurrenciesListAsync,
    gtmPushEvent,
    gtmAlertPushEvent,
    getClientWidth,
    getScrollTop,
    getURLParameter,
    insertURLParameter,
    removeURLParameter,
    unsetScrollPosition,
    logSAMError,
    criteoPushEvent,
    setIOSScrollLock,
    removeIOSScrollLock,
    addAuctionSiteDLVs,
    getProperTimeFormatFromMinutes,
    truncateString,
    syncCanonicalUrl,
    isDateCurrentDay,
    formatDateToDateAndTimeWithSeconds    
} from '@Components/Utilities/SandhillsUtility';
import { setupBidCallerPreBiddingSignalRHub } from '@Components/Utilities/BidCallerPreBidding';
import { changeWatchedAuctionGroups, getBidStatusBannerQueryString, logAuctionMessage, patchInUpdatedAuctionData, setupAuctionTimeBiddingSignalRHub, subscribeToSignalRNotConnectedFallback } from '@Components/Utilities/AuctionTimeBidding';

const EmployeeCustomMetadataModal = loadable(() => import('@Components/Shared/Modals/EmployeeCustomMetadataModal'));
const MarketAnalysis = loadable(() => import('@Components/DataAnalysis/MarketAnalysis'));
const FacetedSearch = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearch'));
const FacetedSearchSelectedFacet = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchSelectedFacet'));
const CalculatorModal = loadable(() => import('@Components/Shared/Modals/CalculatorModal'), true);
const ConflictingSpecsModal = loadable(() => import('@Components/Shared/Modals/ConflictingSpecsModal'), true);
const LoanApprovalEstimateBanner = loadable(() => import('@Components/Shared/LoanApprovalEstimate/LoanApprovalEstimateBanner'));
const LoanApprovalEstimateModal = loadable(() => import('@Components/Shared/Modals/LoanApprovalEstimateModal'), true);
const LoanApprovalEstimateDisclaimer = loadable(() => import('@Components/Shared/LoanApprovalEstimate/LoanApprovalEstimateDisclaimer'));
const LocalizedSimilarListings = loadable(() => import('@Components/Listings/LocalizedSimilarListings'));
const FTISlider = loadable(() => import('@Components/WantToBuy/FTIWantToBuys'));
const QuickBidInfoModal = loadable(() => import('@Components/Shared/Modals/QuickBidInfoModal'), true);
const EnableQuickBidModal = loadable(() => import('@Components/Shared/Modals/EnableQuickBidModal'), true);
const SEOLinks = loadable(() => import('@Components/Listings/SEOLinks'));
const WebListing_LargeCard = loadable(() => import('@Components/Listings/WebListing_LargeCard'));
const WebListing_SmallCard = loadable(() => import('@Components/Listings/WebListing_SmallCard'));
const WebListing_ThumbCard = loadable(() => import('@Components/Listings/WebListing_ThumbCard'));
const WebListing_Boxed = loadable(() => import('@Components/Listings/WebListing_Boxed'));
const WebListing = loadable(() => import('@Components/Listings/WebListing'));
const EventListing = loadable(() => import('@Components/Events/EventListing'));
const StolenEquipmentListCard = loadable(() => import('@Components/StolenEquipment/StolenEquipmentListCard'));
const EmailModal = loadable(() => import('@Components/Shared/Modals/EmailModal'), true);
const BiddingAgreementModal = loadable(() => import('@Components/Shared/Modals/BiddingAgreementModal'), true);
const DistanceLocationModal = loadable(() => import('@Components/Shared/Modals/DistanceLocationModal'));
const BuyersPremiumModal = loadable(() => import('@Components/Shared/Modals/BuyersPremiumModal'), true);
const AlertModal = loadable(() => import('@Components/Shared/Modals/AlertModal'), true);
const PhotoListing_SimilarListings = loadable(() => import('@Components/Listings/PhotoListing_SimilarListings'));
const EmbeddedYouTubePlaylist = loadable(() => import('@Components/Shared/EmbeddedYouTubePlaylist'));
const TimeZoneModal = loadable(() => import('@Components/Shared/Modals/UserTimeZoneModal'));
const AlertPreferenceModal = loadable(() => import('@Components/Shared/Modals/AlertPreferenceModal'));
const ListingBanner = loadable(() => import('@Components/Shared/ListingBanner'));
const BetaSiteCookieWarning = loadable(() => import('@Components/Shared/BetaSiteCookieWarning'));
const FTIModal = loadable(() => import('@Components/Shared/Modals/FTIInformationModal'));
const RequestACharterModal = loadable(() => import('@Components/Shared/Modals/RequestACharterModal'));
const LocationMap = loadable(() => import('@Components/Maps/LocationMap'));
const CharterMapModal = loadable(() => import('@Components/Shared/Modals/CharterMapModal'));
const FacetedSearchMobileMenu = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchMobileMenu'));
const FTIWTBPlug = loadable(() => import('@Components/Shared/FTIWTBPlug'));
const BetaSiteCookieButton = loadable(() => import('@Components/Shared/BetaSiteCookieButton'));
const ResultsDropdown = loadable(() => import('@Components/Shared/ResultsDropdown'));
const SortDropdown = loadable(() => import('@Components/Shared/SortDropdown'));
const CircularProgress = loadable(() => import('@mui/material/CircularProgress'));
const Backdrop = loadable(() => import('@mui/material/Backdrop'));
const ListPageBlogs = loadable(() => import('@Components/Listings/ListPageBlogs'));
const RelevantPartsLink = loadable(() => import('@Components/Listings/RelevantPartsLink'));

const MemoComplexBreadcrumb = React.memo(ComplexBreadcrumb);

/* 
    Important things to note with MemoFacetedSearch and MemoShowClosestFirstToggle
    1. Not all props need to be included in the comparator - If a props does not affect the visual output of the component, you don't need to include it
    2. If you have a heavy prop like an array or object, make sure that you are wrapping it with useMemo - see memoSearchFields, memoSelectedFacets, etc
    3. If you have a function prop that could affect rendering/child behavior, make sure to wrap it in a useCallback, otherwise the component will rerender everytime the parent renders no matter what.
*/
const MemoFacetedSearch = React.memo(FacetedSearch, (prev, next) => {
    if (prev.SelectedSortValue !== next.SelectedSortValue) return false;
    if (prev.ListingsCount !== next.ListingsCount) return false;
    if (prev.disabled !== next.disabled) return false;
    if (prev.showMobileFilters !== next.showMobileFilters) return false;
    if (prev.IsAttachmentAjaxSearch !== next.IsAttachmentAjaxSearch) return false;
    if (prev.IsEventsPage !== next.IsEventsPage) return false;
    if (prev.TurnOffDefaultSaveThisSearch !== next.TurnOffDefaultSaveThisSearch) return false;
    if (prev.KeywordSearchParamsDetected !== next.KeywordSearchParamsDetected) return false;
    if (prev.listingType !== next.listingType) return false;
    if (prev.IsDefaultSearch !== next.IsDefaultSearch) return false;

    if (prev.SearchFields !== next.SearchFields) return false;
    if (prev.SelectedFacets !== next.SelectedFacets) return false;
    if (prev.SortOrderItems !== next.SortOrderItems) return false;

    if (prev.savedSearchData !== next.savedSearchData) return false;

    if (prev.runSearch !== next.runSearch) return false;
    if (prev.clearAll !== next.clearAll) return false;
    if (prev.clearFacet !== next.clearFacet) return false;
    if (prev.toggleMobileFilter !== next.toggleMobileFilter) return false;
    if (prev.highlightSelectedFacets !== next.highlightSelectedFacets) return false;

    return true;
});

const MemoShowClosestFirstToggle = React.memo(ShowClosestFirstToggle, (prev, next) => {
    if (prev.SelectedValue !== next.SelectedValue) return false;
    if (prev.DistanceSortIsSelected !== next.DistanceSortIsSelected) return false;
    if (prev.displaySCFInput !== next.displaySCFInput) return false;

    if (prev.IsFTISearch !== next.IsFTISearch) return false;
    if (prev.IsRentalSearch !== next.IsRentalSearch) return false;
    if (prev.isFractionalSearch !== next.IsFractionalSearch) return false;
    if (prev.IsOnlineAuctionSearch !== next.IsOnlineAuctionSearch) return false;
    if (prev.IsOnSiteAuctionSearch !== next.IsOnSiteAuctionSearch) return false;
    if (prev.IsDismantledSearch !== next.IsDismantledSearch) return false;
    if (prev.AuctionResultsPage !== next.AuctionResultsPage) return false;
    if (prev.IsEventCatalog !== next.IsEventCatalog) return false;

    if (prev.IsDesktop !== next.IsDesktop) return false;
    if (prev.UseNewListPageDesign !== next.UseNewListPageDesign) return false;

    if (prev.CanViewFTI !== next.CanViewFTI) return false;
    if (prev.DisplayShowClosestFirstByConsent !== next.DisplayShowClosestFirstByConsent) return false;

    if (prev.SortOrderItems !== next.SortOrderItems) return false;
    if (prev.ListingsToggles !== next.ListingsToggles) return false;

    if (prev.OnChange !== next.OnChange) return false;
    if (prev.toggleFTISearch !== next.toggleFTISearch) return false;
    if (prev.toggleMobileSort !== next.toggleMobileSort) return false;
    if (prev.setDisplaySCFInput !== next.setDisplaySCFInput) return false;
    if (prev.setShowDistanceModal !== next.setShowDistanceModal) return false;
    if (prev.setShowAllSpecs !== next.setShowAllSpecs) return false;
    if (prev.setExpandAllDescriptions !== next.setExpandAllDescriptions) return false;

    if (prev.requestLocationOnMount !== next.requestLocationOnMount) return false;

    return true;
});

const MemoWebListing_GridCard = React.memo(WebListing_GridCard);
const MemoWebListing_LargeCard = React.memo(WebListing_LargeCard);
const MemoWebListing_SmallCard = React.memo(WebListing_SmallCard);
const MemoWebListing_ThumbCard = React.memo(WebListing_ThumbCard);
const MemoWebListing_Boxed = React.memo(WebListing_Boxed);
const MemoWebListing = React.memo(WebListing);
const MemoEventListing = React.memo(EventListing);
const MemoStolenEquipmentListCard = React.memo(StolenEquipmentListCard);

const MemoRelevantPartsLink = React.memo(RelevantPartsLink);
const MemoListPageBlogs = React.memo(ListPageBlogs);
const MemoFTIWTBPlug = React.memo(FTIWTBPlug);
const MemoLoanApprovalEstimateDisclaimer = React.memo(LoanApprovalEstimateDisclaimer);

const constants = {
    ActivationUTCDateTime: 'ActivationUTCDateTime',
    AirportDetails: 'AirportDetails',
    AirportID: 'AirportID',
    Animal: 'Animal',
    Bounds: 'Bounds',
    AuctionResults: 'Auction Results',
    City: 'City',
    CityID: 'CityID',
    ClassificationCategory: 'ClassificationCategory',
    CompanyName: 'CompanyName',
    Country: 'Country',
    Condition: 'Condition',
    Crop: 'Crop',
    CurrentPageReplacement: '##CurrentPage##',
    Distance: 'Distance',
    DSCompanyID: 'DSCompanyID',
    EventCategoryID: 'EventCategoryID',
    EventID: 'EventID',
    EventType: 'eventtype',
    FacetedSpecGroup: '&FacetedSpecGroup=',
    FilterByState: 'FilterByState',
    InventoryType: 'InventoryType',
    IsSegmentSearch: 'IsSegmentSearch',
    Latitude: 'lat',
    ListingsCountReplacement: '##ListingsCount##',
    ListingsTitleFormat: translator.translate('sandhills.xthroughyofzlistings').toString(),
    ListingType: 'ListingType',
    LocationID: 'LocationID',
    Longitude: 'long',
    MaxDescriptionLength: 65,
    MiddleIndex: 4,
    Model: 'Model',
    ModelGroup: 'ModelGroup',
    OtherItems: 'OtherItems',
    Page: 'page',
    PageSize: 'PageSize',
    Print: 'print',
    ScopeCategoryIDs: 'ScopeCategoryIDs',
    SoldUTCDateTime: 'SoldUTCDateTime',
    Sort: 'sort',
    State: 'State',
    TotalPagesReplacement: '##TotalPages##',
    UseCurrentLocation: 'UseCurrentLocation',
    PreATSoldUTCDateTime: 'PreATSoldUTCDateTime',
    WholeSaleOptions: 'wso',
    PreATResults: 'patr',
    FleetEvaluatorSortValue: 'fesv',
    AllListings: 'AL',
};



function ListingsView(props) {
    const keywordSearchState = useRecoilValue(atoms.keywordSearchStateAtom);
    const windowSize = useWindowSize();
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const isRY = currentSite?.Site?.IsRentalYard;
    const [marketAnalysisURL, setMarketAnalysisURL] = useState(props.ListPageAjaxModel.MarketAnalysis?.ChartAjaxRoute);
    const [useBetaSiteCookie, setUseBetaSiteCookie] = useState(false);
    const showEmployeeOnlyBetaSiteToggle = useRecoilValue(atoms.showEmployeeOnlyBetaSiteToggle);
    const [listings, dispatchListings] = useReducer(listingsReducer, props.ListPageAjaxModel.Listings);
    const listingsRef = useRef(listings);
    const [pastListings, setPastListings] = useState(props.ListPageAjaxModel.Listings.filter(listing => listing.IsPastEvent));
    const [liveListings, setLiveListings] = useState(props.ListPageAjaxModel.Listings.filter(listing => !listing.IsPastEvent));
    const liveEventIDs = useRef([]);
    const endedLiveEventInfo = useRef([]);
    const [searchData, setSearchData] = useState({
        DoubleClickAdCollectionViewModel: props.ListPageAjaxModel.DoubleClickAdCollectionViewModel,
        ListingCountTitle: props.ListPageAjaxModel.ListingCountTitle,
        ShippingEstimates: [],
        SortOrderItems: props.ListPageAjaxModel.SortOrderItems,
        distanceSortID: props.ListPageAjaxModel.distanceSortID,
        defaultSortID: props.ListPageAjaxModel.defaultSortID,
        CurrentPage: props.ListPageAjaxModel.CurrentPage,
        DisplayShowClosestFirstByConsent: props.ListPageAjaxModel.DisplayShowClosestFirstByConsent,
        TotalPages: props.ListPageAjaxModel.TotalPages,
        PageSize: props.ListPageAjaxModel.PageSize,
        ListingsCount: props.ListPageAjaxModel.ListingsCount,
        LocalizedSimilarListingsInfo: props.ListPageAjaxModel.LocalizedSimilarListingsInfo,
        ShowBlurb: props.BlurbModel && props.BlurbModel.ShowBlurb,
        HighLowAverageURL: props.ListPageAjaxModel.HighLowAverageURL,
        SimilarListingsModel: props.ListPageAjaxModel.SimilarListingsModel,
        CategoryInformation: props.ListPageAjaxModel.CategoryInformation,
        SortApplied: props.ListPageAjaxModel.SortApplied,
        DealerHeaderInfo: props.InventoryHeader,
        LocationString: props.ListPageAjaxModel.LocationString,
        MarketAnalysis: props.ListPageAjaxModel.MarketAnalysis,
        ListingLocations: [],
        ListingMapData: [],
        DefaultZoom: props.ListPageAjaxModel.DefaultZoom,
        DefaultCenter: props.ListPageAjaxModel.DefaultCenter,
        DynamicBlurb: props.ListPageAjaxModel.DynamicBlurb
    });
    const ShowAV = currentSite?.Site?.IsController;

    const [criteriaData, setCriteriaData] = useState({
        MetaTitle: props.FacetedSearchAjaxModel.MetaTitle,
        PageHeader: props.FacetedSearchAjaxModel.PageHeader,
        InternalLinkingModel: props.FacetedSearchAjaxModel.InternalLinkingModel,
        ClearAllURL: props.FacetedSearchAjaxModel.ClearAllURL,
        SegmentBaseUrl: props.FacetedSearchAjaxModel.SegmentBaseUrl,
        BreadCrumbs: props.FacetedSearchAjaxModel.BreadCrumbs,
        SearchFields: props.FacetedSearchAjaxModel.SearchFields,
        SelectedFacets: props.FacetedSearchAjaxModel.SelectedFacets,
        ConflictingSpecs: props.FacetedSearchAjaxModel.ConflictingSpecs,
        ConflictingFacets: props.FacetedSearchAjaxModel.ConflictingFacets,
        PendingSearchCriteria: [],
        SelectedSortValue: props.FacetedSearchAjaxModel.SelectedSortValue,
        SearchBaseURL: props.SearchBaseURL,
        SearchPageURL: props.FacetedSearchAjaxModel.SearchPageURL,
        IsDefaultSearch: props.FacetedSearchAjaxModel.IsDefaultSearch,
        SAMModel: props.FacetedSearchAjaxModel.SAMModel,
        LoginLink: props.FacetedSearchAjaxModel.LoginLink,
        ClassificationToForceScope: props.FacetedSearchAjaxModel.ClassificationToForceScope,
        TurnOffDefaultSaveThisSearch: props.FacetedSearchAjaxModel.TurnOffDefaultSaveThisSearch,
        IsAttachmentAjaxSearch: props.IsAttachmentPage,
        IsOtherItemsPage: props.FacetedSearchAjaxModel.IsOtherItemsPage,
        IsAuctionResults: props.FacetedSearchAjaxModel.IsAuctionResults,
        RelevantPartsLink: props.FacetedSearchAjaxModel.RelevantPartsLink,
        RelevantPartsTitle: props.FacetedSearchAjaxModel.RelevantPartsTitle,
        PriceBlurb: props.FacetedSearchAjaxModel.PriceBlurbs,
        KeywordSearchParamsDetected: props.FacetedSearchAjaxModel?.KeywordSearchParamsDetected
    });

    const eventsTimer = useRef(null);
    const listBlurbRef = useRef(null);
    const locationMapRef = useRef(null);
    const [mapDataLoading, setMapDataLoading] = useState(!!props.ListingsMapDataUrl);
    const pageVisibilityHiddenTime = useRef(null);
    const [isDistanceModalSearch, setIsDistanceModalSearch] = useState(searchData.LocationString?.length > 0 && (!props.IsDistanceSortForStateAndCitySearches || (getURLParameter(criteriaData.SearchBaseURL, constants.State).split('|').filter(s => s)?.length != 1 && props.IsDistanceSortForStateAndCitySearches)));
    const openFacetedModalOnLoad = props.FacetedSearchAjaxModel.SearchFields.some(field => field.OpenShowAllModalOnLoad);
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);
    const [isMounted, setIsMounted] = useState(false);
    const [useCurrentLocation, setUseCurrentLocation] = useState(false);
    const [isPopState, setIsPopState] = useState(false);
    const [alertModalStatus, setAlertModalStatus] = useState({ show: false, message: '' });
    const [showMobileFilters, setShowMobileFilters] = useState(openFacetedModalOnLoad);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [listingsSearchInProgress, setListingsSearchInProgress] = useState(false);
    const [criteriaSearchInProgress, setCriteriaSearchInProgress] = useState(false);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [showDistanceModal, setShowDistanceModal] = useState(false);
    const [showDistanceSortError, setShowDistanceSortError] = useState(false);
    const [showLAE, setShowLAE] = useRecoilState(atoms.showLae);
    const [showConflictingSpecsModal, setShowConflictingSpecsModal] = useState(false);
    const [showShippingDisclaimer, setShowShippingDisclaimer] = useState(false);
    const [showVIPDisclaimer, setShowVIPDisclaimer] = useState(props.FacetedSearchAjaxModel.ShowVIPDisclaimer);
    const [buyersPremiumData, setBuyersPremiumData] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [highlightSelectedFacets, setHighlightSelectedFacets] = useState(false);
    const [showBiddingAgreementModal, setShowBiddingAgreementModal] = useState(false);
    const [biddingAgreementProps, setBiddingAgreementProps] = useState(null);
    const [selectedInquiry, setSelectedInquiry] = useState('');
    const [selectedContactType, setSelectedContactType] = useState(contactType.Retail);
    const [userWatchlist, setUserWatchlist] = useState(props.IsWatchList && !props.IsRecommendedListingsSearch ? props.ListPageAjaxModel.Listings : []);
    const [pushStateToWindowHistory, setPushStateToWindowHistory] = useState(true);
    const [financialCalculatorData, setFinancialCalculatorData] = useState({ showFinancialCalculator: false });
    const [selectedCompareListings, setSelectedCompareListings] = useState(props.SelectedCompareListings);
    const [loadImagesForPrint, setLoadImagesForPrint] = useState(false);
    const [showShareButtons, setShowShareButtons] = useState(false);
    const [userLocation, setUserLocation] = useState({
        longitude: currentUser?.CurrentLocation?.Coordinates?.Longitude,
        latitude: currentUser?.CurrentLocation?.Coordinates?.Latitude
    });
    const [showLoadingRing, setShowLoadingRing] = useState(false);
    const [ftiWTBs, setFTIWTBs] = useState(props.FTIWTBs);

    const [showTimeZoneModal, setShowTimeZoneModal] = useState(false);
    const [showFTIModal, setShowFTIModal] = useState(props.ShowFTIInfo);
    const [requestExclusiveWholesaleAccess, setRequestExclusiveWholesaleAccess] = useState({
        hasRequestedAccess: false,
        message: null
    });

    const [returnFocusRef, setReturnFocusRef] = useState();
    const ref = useRef([]);

    const [savedSearchData, setSavedSearchData] = useState({
        Message: '',
        PendingSave: false,
        Loading: false,
        ShowModal: false,
        SearchCompleted: false,
        WebSavedSearches: []
    });

    const [showEmployeeCustomMetadataModal, setShowEmployeeCustomMetadataModal] = useState(false);
    const [showAlertPreferenceModal, setShowAlertPreferenceModal] = useState(false);
    const [eventIDForAlertPreferenceModal, setEventIDForAlertPreferenceModal] = useState(0);
    const [eventCategoryIDForAlertPreferenceModal, setEventCategoryIDForAlertPreferenceModal] = useState(0);
    const [notificationSettings, setNotificationSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [toggleCloseMA, setToggleCloseMA] = useState(false);
    const [forceReloadMA, setForceReloadMA] = useState('');
    const [useGridView, setUseGridView] = useState(props.UseNewListPageDesign && !windowSize.isMobile);
    const [CardType, setCardType] = useState(windowSize.isMobile && props.DefaultMobileView && props.UseNewListPageDesign ? "MOBILE" : props.UseNewListPageDesign ? 'GRID' : props.ListingLayout.toUpperCase());
    const loanApprovalBannerIndex = (useGridView && !windowSize.isMobile) ? 3 : 1;
    const RVWizardBannerIndex = (useGridView && !windowSize.isMobile) ? 3 : 1;
    const [newView, setNewView] = useState(windowSize.isMobile && !props.DefaultMobileView);
    const [watchlistCount, setWatchlistCount] = useState(0);
    const [similarListings, setSimilarListings] = useReducer(listingsReducer, searchData.SimilarListingsModel?.SimilarListings);
    const [isFTISearch, setIsFTISearch] = useState(props.ListPageAjaxModel.IsFTISearch);
    const isRentalSearchInitial = (() => {
        const listingType = getURLParameter(criteriaData.SearchPageURL, "ListingType");
        let allowedTypes = 'For Rent|For Lease';
        if ((!props.RentalToggleText?.includes(translator.translate('sandhills.rent')) && !props.RentalToggleText?.includes(translator.translate('sandhills.hire'))) || currentSite?.Site?.IsAviationTrader) {
            allowedTypes = 'For Lease';
        }
        return (listingType && allowedTypes === listingType);
    });
    const isFractionalSearchInitial = (() => {
        const listingType = getURLParameter(criteriaData.SearchPageURL, "ListingType");
        let allowedTypes = 'For Fractional Ownership';
        return (listingType && allowedTypes === listingType);
    });
    const [isRentalSearch, setIsRentalSearch] = useState(isRentalSearchInitial);
    const [isFractionalSearch, setIsFractionalSearch] = useState(isFractionalSearchInitial);
    const [isOnlineAuctionSearch, setIsOnlineAuctionSearch] = useState(getURLParameter(criteriaData.SearchPageURL, constants.EventCategoryID) == "7");
    const [isOnSiteAuctionSearch, setIsOnSiteAuctionSearch] = useState(getURLParameter(criteriaData.SearchPageURL, constants.EventCategoryID) == "8");
    const [isDismantledSearch, setIsDismantledSearch] = useState(getURLParameter(criteriaData.SearchPageURL, constants.Condition) == "DISMANTLED");
    const [displaySCFInput, setDisplaySCFInput] = useState(criteriaData.SelectedSortValue == searchData.distanceSortID);
    const [isEventSaleCatalogPage, setIsEventSaleCatalogPage] = useState(props.IsEventSaleCatalogPage);
    const [showNumResultsDisplayedSelect, setShowNumResultsDisplayedSelect] = useState(props.ShowNumResultsDisplayedSelect);
    const [showRequestAQuoteModal, setShowRequestAQuoteModal] = useState(props.ShowRequestAQuoteModalOnLoad);
    const setIsAuctionResult = useSetRecoilState(atoms.isAuctionResult);
    const [showMapViewModal, setShowMapViewModal] = useState(false);
    const [locationState, setLocationState] = useState(null);
    const worldWideBannerListingIndex = listings?.findIndex(listing => listing.GroupBanner?.DisplayGroup == 'WorldWide');
    const worldWideBannerIndex = useGridView && worldWideBannerListingIndex >= 0 ? worldWideBannerListingIndex - (worldWideBannerListingIndex % 4) : -1;
    const [expandAllDescriptions, setExpandAllDescriptions] = useState(props.ShowFullDescription);
    const [showAllSpecs, setShowAllSpecs] = useState(props.ShowAllSpecs);
    const [defaultMobileView, setDefaultMobileView] = useState(props.DefaultMobileView);
    const [stickyHeaderSet, setStickyHeaderSet] = useState(false);
    const [hasExampleKeywordSearchPhrases, setHasExampleKeywordSearchPhrases] = useState(keywordSearchState.AutocompleteTerms && keywordSearchState.AutocompleteTerms.length > 0);
    const listingType = getURLParameter(criteriaData.SearchPageURL, "ListingType");
    const memoSearchFields = useMemo(() => criteriaData.SearchFields || [], [criteriaData.SearchFields]);
    const memoSelectedFacets = useMemo(() => criteriaData.SelectedFacets || [], [criteriaData.SelectedFacets]);
    const memoSortOrderItems = useMemo(() => searchData.SortOrderItems || [], [searchData.SortOrderItems]);
    const pricingInfoRef = useRef(new Map());
    const [showQuickBidInfoModal, setShowQuickBidInfoModal] = useState(false);
    const [showQuickBidActivationModal, setShowQuickBidActivationModal] = useState(false);
    const [quickBiddingEnabled, setQuickBiddingEnabled] = useState(props.IsQuickBidEnabled);    
    const [quickBiddingError, setQuickBiddingError] = useState(false);    

    const waitingForQuickBidAttemptResponse = useRef(false);

    const setWaitingForQuickBidAttemptResponseFlag = (submittedQuickBid) => {
        waitingForQuickBidAttemptResponse.current = submittedQuickBid;
    }

    const toggleEmployeeCustomMetadataModal = () => {
        setShowEmployeeCustomMetadataModal(showEmployeeCustomMetadataModal => !showEmployeeCustomMetadataModal);
    };

    // Filter out FacetedSearchAdvertisement from AdUnits when on mobile to prevent slots.length != adUnits.length mismatch
    const memoDoubleClickAdCollection = useMemo(() => {
        const collection = searchData.DoubleClickAdCollectionViewModel?.DoubleClickAdCollection;
        if (!collection) {
            return collection;
        }

        let adUnits = collection.AdUnits;

        if (windowSize.isMobile) {
            const facetedSearchAd = searchData.DoubleClickAdCollectionViewModel?.FacetedSearchAdvertisement;
            if (facetedSearchAd && adUnits) {
                adUnits = adUnits.filter(ad => ad.AdUnitCode !== facetedSearchAd.AdUnitCode);
            }
        }

        if (similarListings?.length) {
            const topAd = searchData.DoubleClickAdCollectionViewModel?.TopAdvertisement;
            if (topAd && adUnits) {
                adUnits = adUnits.filter(ad => ad.AdUnitCode !== topAd.AdUnitCode);
            }
        }

        if (adUnits === collection.AdUnits) {
            return collection;
        }

        return {
            ...collection,
            AdUnits: adUnits
        };
    }, [searchData.DoubleClickAdCollectionViewModel, windowSize.isMobile, similarListings]);

    const [countdownListings, setCountdownListings] = useState([]);    
    const [preventCountdownDueToProcessingAuctionUpdate, setPreventCountdownDueToProcessingAuctionUpdate] = useState(false);

    // Because of the SetTimeout closure, the potentially spun up setTimeouts will have the old state value. So we need to store whatever is most current in a ref..
    const AuctionInformationUpdateKeys = useRef([]);
    const auctionInformationTimeout = useRef(0);
    let lastAuctionUpdatedReceived = useRef(new Date(0));
    let auctionInformationErrorCount = 0;
    let auctionInformationEmptyResponseCount = 0;

    const [forceAuctionUpdatePollingForListingsInAuctionUpdateCache, setforceAuctionUpdatePollingForListingsInAuctionUpdateCache] = useState(false);
         
    // For the AT Updates we are just going to be patching in the latest information recieved, but we need the full object
    // This will just store those objects that we can back from the server on page load and update them as needed
    const latestAuctionBiddingData = useRef([]);
    const initializedATSignalR = useRef(false);
    const [dynamicBlurbMobileSplitIndex, setDynamicBlurbMobileSplitIndex] = useState(props.ListPageAjaxModel?.DynamicBlurbMobileSplitIndex || 0)

    const setStickyObserver = (target, stickyElement) => {
        if (target && stickyElement) {
            const stickyElementLocation = stickyElement.getBoundingClientRect().top;
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting && getScrollTop() >= stickyElementLocation) {
                        stickyElement.classList.add('sticky-top-menu');
                        setStickyHeaderSet(true);
                    } else {
                        stickyElement.classList.remove('sticky-top-menu');
                        setStickyHeaderSet(false);
                    }
                });
            }, {
                root: null,
                threshold: 0.75,
            });

            observer.observe(target);
            return observer;
        }
    };

    const signalRNotConnectedFallback = () => {
        if (!forceAuctionUpdatePollingForListingsInAuctionUpdateCache) {
            setforceAuctionUpdatePollingForListingsInAuctionUpdateCache(true); //Once this gets set to true, never go back.        
        }
    }

    const listingShouldUseAuctionUpdatePolling = (listing) => {
        return forceAuctionUpdatePollingForListingsInAuctionUpdateCache && listing.AuctionBiddingData?.TimeRemaining > -30 && isDateCurrentDay(listing.AuctionBiddingData?.ProjectedEndDateTime);
    }

    useEffect(() => {
        setHasExampleKeywordSearchPhrases(keywordSearchState.AutocompleteTerms && keywordSearchState.AutocompleteTerms.length > 0);
    }, [keywordSearchState]);

    useEffect(() => {
        const target = document.querySelector('.non-sticky');
        const listingOptionBar = document.querySelector('.listing-option-bar');
        const observer = setStickyObserver(target, listingOptionBar);

        return () => {
            observer?.disconnect();
        };
    }, [hasExampleKeywordSearchPhrases]);

    useEffect(() => {
        setShowShippingDisclaimer(false);
        if (loadImagesForPrint) {
            if (listings.every(listing => listing.ImagesLoaded || (useGridView && listing.ListingImageModel.ImageUrl.length > 1))) {
                window.print();
                setLoadImagesForPrint(false);
            }
        }

        listingsRef.current = listings;
    }, [listings]);

    useEffect(() => {
        if (criteriaData.IsAuctionResults !== null) {
            setIsAuctionResult(criteriaData.IsAuctionResults);
        }
    }, [criteriaData]);

    useEffect(() => {
        setIsRentalSearch(isRentalSearchInitial());
        setIsFractionalSearch(isFractionalSearchInitial())
        setIsOnlineAuctionSearch(getURLParameter(criteriaData.SearchPageURL, constants.EventCategoryID) == "7");
        setIsOnSiteAuctionSearch(getURLParameter(criteriaData.SearchPageURL, constants.EventCategoryID) == "8");
        setIsDismantledSearch(getURLParameter(criteriaData.SearchPageURL, constants.Condition) == "DISMANTLED");
    }, [criteriaData.SearchPageURL]);

    useEffect(function facetedSearch() {
        if (userLocation.latitude != currentUser?.CurrentLocation?.Coordinates?.Latitude
            || userLocation.longitude != currentUser?.CurrentLocation?.Coordinates?.Longitude
            && currentUser?.CurrentLocation?.Coordinates?.Latitude
            && currentUser?.CurrentLocation?.Coordinates?.Longitude) {
            setUserLocation({
                longitude: currentUser?.CurrentLocation?.Coordinates?.Longitude,
                latitude: currentUser?.CurrentLocation?.Coordinates?.Latitude
            });
        }
        if (isMounted && !isPopState && !showDistanceModal) {
            runSearch(false);
        }
    }, [currentUser.CurrentLocation, criteriaData.SelectedSortValue]);

    useEffect(function onCurrentPageChange() {
        paginate(true);
    }, [searchData.CurrentPage]);

    useEffect(function onPageSizeChange() {
        paginate(false);
    }, [searchData.PageSize]);

    useEffect(function pushWindowHistory() {
        if (isMounted && !isPopState && !listingsSearchInProgress && !criteriaSearchInProgress) {
            const currentOrigin = window.location.origin;
            const requestedOrigin = new URL(criteriaData.SearchPageURL, window.location.origin).origin;
            const isCORS = currentOrigin !== requestedOrigin;

            if (pushStateToWindowHistory && !isCORS) {
                try {
                    window.history.pushState({
                        Listings: listings,
                        ...searchData,
                        ...criteriaData,
                        IsFTISearch: isFTISearch,
                        Timestamp: new Date()
                    }, criteriaData.MetaTitle, criteriaData.SearchPageURL);

                    syncCanonicalUrl(criteriaData.SearchPageURL);

                    if (searchData.ListingsCount === 0 && !props.Message) {
                        gtmPushEvent(gtmEventType.noListingsFound);
                    }
                } catch (error) {
                    setPushStateToWindowHistory(false);
                    window.removeEventListener('popstate', handlePopState);
                    window.history.pushState({}, criteriaData.MetaTitle, criteriaData.SearchPageURL);
                    syncCanonicalUrl(criteriaData.SearchPageURL);
                    console.log('window history state disabled');
                }
            } else if (!isCORS) {
                window.history.pushState({}, criteriaData.MetaTitle, criteriaData.SearchPageURL);
                syncCanonicalUrl(criteriaData.SearchPageURL);
            }
        }
    }, [listingsSearchInProgress, criteriaSearchInProgress]);

    useEffect(() => {
        if (windowSize.isMobile && defaultMobileView && props.UseNewListPageDesign) {
            setCardType('MOBILE');
        } else if (props.UseNewListPageDesign) {
            setCardType("GRID");
            setUseGridView(true);
        }
    }, [windowSize.isMobile]);

    useEffect(() => {
        if (getCookie(cookieNames.useBetaSiteCookie) == '1') {
            setUseBetaSiteCookie(true);
        }
        //if (windowSize.isMobile) { COMMENTING OUT UNTIL THIS IS NEEDED AGAIN.
        //    onINP(sendINPData, { reportAllChanges: true });
        //}

        const dsCompanyID = getURLParameter(window.location.search, constants.DSCompanyID);
        const locationID = getURLParameter(window.location.search, constants.LocationID);
        if((dsCompanyID || locationID) && !currentSite.Site.IsHostedSite) {
            gtmAlertPushEvent(gtmEventType.dealerPage, constants.DSCompanyID, searchData.DealerHeaderInfo.DSCompanyID, constants.CompanyName, searchData.DealerHeaderInfo.CompanyName);
        }   
        setWindowOverrides();
        window.addEventListener('popstate', handlePopState);
        getUserWatchlist();
        if (!props.ListPageAjaxModel?.SkipAuctionDataGetToDetermineBidStatus) {
            getListingAuctionData(listings, dispatchListings);
        }
        if (similarListings) {
            getListingAuctionData(similarListings, setSimilarListings);
        }

        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };

        ready(() => {
            switch (CardType) {
                case 'LARGECARD':
                    WebListing_LargeCard.preload();
                case 'SMALLCARD':
                    WebListing_SmallCard.preload();
                case 'THUMBCARD':
                    WebListing_ThumbCard.preload();
                case 'BOXED':
                    WebListing_Boxed.preload();
                case 'STOLENEQUIPMENT':
                    StolenEquipmentListCard.preload();
                case 'EVENTS':
                    EventListing.preload();
                case 'GRID':
                case 'MOBILE':
                    // statically importing for LCP considerations on the most common card type
                    break;
                case 'WEBLISTING':
                default:
                    WebListing.preload();
            }

            if (!props.IsHostedSite && props.InventoryHeader) {
                BiddingAgreementModal.preload();
            }

            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
            getShippingEstimates(props.ListPageAjaxModel.ListingIdsForShippingEstimates);
            getAuctionProgress(listings);
            setIsMounted(true);
            fetchMapData(props.SearchBaseURL);
            if (props.OpenSCFModalOnLoad) {
                onDistanceLocationModalToggle();
            }
            if (props.BidCallerPreBiddingHubUrl.trim() !== "") {
                let listingIds = listings.reduce((g, l) => g += l.Id + ",", '');
                setupBidCallerPreBiddingSignalRHub(props.BidCallerPreBiddingHubUrl, props.ShowEmployeeOnlyInfo, listingIds, resetAuctionDataForListing, calculateEstimatedTimes);
            }

            addAuctionDLVs();

        });
        let compare = getCookie(cookieNames.compare);
        if (compare && selectedCompareListings) {
            let cookieParts = compare?.split('&');
            let idList = cookieParts[0]?.replace("IDs=", "")?.split(',');
            let includedIDs = selectedCompareListings?.map(x => x.ListingID.toString());
            if (selectedCompareListings && idList && selectedCompareListings.length < idList.length) {
                let ids = idList.filter(id => !includedIDs.includes(id));
                for (let i = 0; i < ids.length; i++) {
                    tryDeleteCompareListingFromCookie(ids[i]);
                }
            }
        }

        const target = document.querySelector('.non-sticky');
        const listingOptionBar = document.querySelector('.listing-option-bar');
        const observer = setStickyObserver(target, listingOptionBar);

        if (props.ShouldPushCriteoEvent && listings && listings.length > 0) {
            criteoPushEvent("viewList", listings.map(listing => listing.Id));
        }

        const handleVisibilityChange = () => {
            // Don't need to do anything on non-auctiontime sites
            if (currentSite?.Site?.IsAuctionTime) {
                if (document.visibilityState === "visible") {
                    if (pageVisibilityHiddenTime.current && ((new Date() - pageVisibilityHiddenTime.current) / 1000) > 300) {
                        logAuctionMessage("Refreshing auction data for page", "Page last shown: ", pageVisibilityHiddenTime.current);
                        // If the page was hidden for more than 5 minutes, refresh the auction data
                        // Method will only make the call if we have any upcoming listings
                        getListingAuctionData(listings, dispatchListings);
                    }
                }
                else {
                    logAuctionMessage("Page Hidden");
                    pageVisibilityHiddenTime.current = new Date();
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
                
        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('resize', cookieBannerResize);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            observer?.disconnect();
            if (props.IsEvents && eventsTimer.current) {
                clearInterval(eventsTimer.current);
            }
        };
    }, []);

    const delayQuery = useMemo(() => debounce(pendingCriteria => {
        runSearch(false, false, false, pendingCriteria, true);
    }, 300), [criteriaData]);
    const delayDistance = useMemo(() => debounce(e => onSortChange(e), 500), []);

    const paginate = (keepPage) => {
        if (isMounted && !isPopState && !listingsSearchInProgress) {
            runSearch(keepPage);
        }
    };

    //const sendINPData = (event) => {
    //    if (event?.value > 200) {
    //        let targets = [];
    //        for (let i = 0; i < event.entries.length; i++) {
    //            let tar = event.entries[i];
    //            if (tar.target != null && tar.target?.outerHTML != null) {
    //                targets.push(tar.target?.outerHTML?.toString().replace(/[^a-zA-Z0-9]/g, "-"));
    //            }
    //        }
    //        event.entries = targets;
    //        event.URL = window.location.href;
    //        sandhillsRequest({
    //            url: routes.CheckSaveINP,
    //            data: { inp: event },
    //            method: 'post',
    //            errorHandling: {
    //                showDetails: false,
    //                useConsole: false
    //            }
    //        }).then((response) => {
    //            //do nothing
    //        }).catch((error) => {
    //            console.log('Request canceled:', error.message);
    //        });
    //    }
    //}

    const changeGridView = () => {
        setUseGridView(!useGridView);
        toggleMobileDefault();
        if (CardType === 'GRID') {
            setCardType('MOBILE');
        } else {
            setCardType("GRID");
        }
    };
    const toggleMobileDefault = () => {
        sandhillsRequest({
            url: routes.SaveListingsPageOptionsSetMobileDefault,
            method: 'post',
            data: {
                defaultMobile: !defaultMobileView
            }
        }).then((data) => {
            setDefaultMobileView(!defaultMobile);
        }).catch((error) => {

        });
    }
    function setUpdateMediaImageCounts(forceSearch) {
        if ((CardType !== 'GRID' && CardType == 'WEBLISTING') || forceSearch) {
            sandhillsRequest({
                url: routes.GetMediaCounts,
                data: { listingIDs: listings.map(listing => listing.Id).join('|') },
                method: 'post',
                errorHandling: {
                    message: 'Error retrieving media counts.',
                    showDetails: false,
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                if (response && response.length > 0) {
                    dispatchListings({
                        type: 'update-listings',
                        payload: [...listings].map(listing => {
                            return {
                                ...listing,
                                ListingImageModel: {
                                    ...listing.ListingImageModel,
                                    Image360Count: response.find(media => media.ListingID == listing.Id)?.Image360Count || 0,
                                    VideoCount: listing.ListingImageModel?.VideoCount > 0 ? listing.ListingImageModel.VideoCount : response.find(media => media.ListingID == listing.Id)?.VideoCount || 0
                                }
                            };
                        })
                    });
                }
            });
        }
    };

    const toggleView = () => {
        setNewView(newView => !newView);
        gtmPushEvent(newView ? 'toggle-condensed' : 'toggle-grid');
        changeGridView();
    };

    const handleSetMapCenter = (lat, lng, distance) => {
        if (locationMapRef.current) {
            locationMapRef.current.setMapCenter(lat, lng, distance);
        }
    }

    const toggleFTISearch = useCallback((type, criteria) => {
        if (!currentUser.CanViewFTI && type == "FTI") {
            setShowFTIModal(true);
        } else {
            setShowLoadingRing(true);
            let pendingCriteria = [];
            Object.entries(criteria).forEach(([fieldName, value]) => {
                pendingCriteria.push({ fieldName, value });
            })
            switch (type) {
                case "FTI":
                    setIsRentalSearch(false);
                    setIsFTISearch(true);
                    break;
                case "FRACTIONAL":
                    setIsRentalSearch(false);
                    setIsFractionalSearch(true);
                    break;
                case "RENT":
                    setIsFTISearch(false);
                    setIsFractionalSearch(false);
                    setIsRentalSearch(true);
                    break;
                case "ONLINE":
                    setIsFTISearch(false);
                    setIsRentalSearch(false);
                    setIsFractionalSearch(false);
                    setIsOnSiteAuctionSearch(false);
                    setIsOnlineAuctionSearch(true);
                    break;
                case "ONSITE":
                    setIsFTISearch(false);
                    setIsRentalSearch(false);
                    setIsFractionalSearch(false);
                    setIsOnlineAuctionSearch(false);
                    setIsOnSiteAuctionSearch(true);
                    break;
                default:
                    setIsFTISearch(false);
                    setIsRentalSearch(false);
                    setIsFractionalSearch(false);
                    setIsOnlineAuctionSearch(false);
                    setIsOnSiteAuctionSearch(false);
                    break;
            }
            delayQuery(pendingCriteria);
        }
    }, [
        currentUser.CanViewFTI,
        delayQuery
    ]);

    const refreshWatchlist = useCallback((listingsOverride) => {
        const listingsToUse = listingsOverride || listings;
        if (!props.IsEvents && !props.IsStolenEquipment && !props.IsHostedSite && (!props.IsWatchList || (props.IsRecommendedListingsSearch || props.IsWatchlistMyBids)) && listingsToUse.length > 0) {
            if (currentUser.IsAuthenticated && searchData.ListingsCount > 0) {
                let ids = listingsToUse.map(listing => listing.Id).join('|');
                sandhillsRequest({
                    url: routes.GetListWatchlist,
                    data: { listingIDs: ids },
                    method: 'get',
                    errorHandling: {
                        message: 'Error retrieving watchlist',
                        showDetails: true,
                        useConsole: true,
                        useAlert: false
                    }
                }).then((response) => {
                    if (response && response.length > 0) {
                        setUserWatchlist(response);
                    }
                });
            }
        }
    }, [
        props.IsEvents,
        props.IsStolenEquipment,
        props.IsHostedSite,
        props.IsWatchList,
        props.IsRecommendedListingsSearch,
        props.IsWatchlistMyBids,
        currentUser.IsAuthenticated,
        searchData.ListingsCount,
    ]);

    const getUserWatchlist = useCallback((listingsOverride) => {
        refreshWatchlist(listingsOverride);

        const listingsToUse = listingsOverride || listings;
        if (!props.IsEvents && !props.IsStolenEquipment && !props.IsHostedSite && (!props.IsWatchList || (props.IsRecommendedListingsSearch || props.IsWatchlistMyBids)) && listingsToUse.length > 0) {
            if (currentUser.IsAuthenticated && searchData.ListingsCount > 0) {
                const token = axios.CancelToken.source();

                const timeoutId = setTimeout(() => {
                    token.cancel('Request timed out');
                }, 3000);

                sandhillsRequest({
                    url: routes.GetWatchListCount,
                    cancelToken: token.token,
                    errorHandling: {
                        message: 'Error retrieving watchlist count',
                        showDetails: true,
                        useConsole: true,
                        useAlert: false
                    }
                }).then((response) => {
                    clearTimeout(timeoutId);
                    setWatchlistCount(response);
                }).catch((error) => {
                    clearTimeout(timeoutId);
                    if (axios.isCancel(error)) {
                        console.log('Request canceled:', error.message);
                    } else {
                        console.error('Request failed:', error);
                    }
                });
            }
        }
    }, [
        refreshWatchlist,
        props.IsEvents,
        props.IsStolenEquipment,
        props.IsHostedSite,
        props.IsWatchList,
        props.IsRecommendedListingsSearch,
        props.IsWatchlistMyBids,
        currentUser.IsAuthenticated,
        searchData.ListingsCount,
    ]);

    const addToWatchlistCount = useCallback(() => {
        setWatchlistCount(watchlistCount + 1);
    }, [watchlistCount]);

    const handleRingChange = (event) => {
        const selectedOptionValue = event.target.value;
        if (selectedOptionValue) {
            window.location.href = selectedOptionValue;
        }
    };

    const watchlistClickDelete = useCallback((listingId) => {
        watchlistOnClick('delete', listingId, '', routes)
            .then((response) => {
                if (response.success) {
                    watchlistDeleteSuccess();
                } else {
                    alert(`${translator.translate('sandhills.tradesites.mvc.watchlistnotfound').toString()} ${translator.translate('sandhills.tradesites.mvc.refreshandtryagain').toString()}`);
                    window.location.reload();
                }
            })
            .catch((error) => {
                console.error('Failed to delete from watchlist:' + error);
            });
    }, [watchlistDeleteSuccess]);

    const watchlistDeleteSuccess = () => {
        runSearch(true);
    };

    const getShippingEstimates = (shippingIds) => {
        if (!props.IsEvents && !props.IsStolenEquipment && shippingIds?.length > 0 && userLocation.latitude && userLocation.longitude) {
            sandhillsRequest({
                url: routes.FR8StarURL,
                method: 'get',
                data: {
                    listingIDs: shippingIds
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                if (response && Object.keys(response).length > 0) {
                    setShowShippingDisclaimer(true);
                    setSearchData(previousState => {
                        return { ...previousState, ShippingEstimates: response };
                    });
                } else {
                    setShowShippingDisclaimer(false);
                }
            }).catch(() => {
                setShowShippingDisclaimer(false);
            });
        } else {
            setShowShippingDisclaimer(false);
        }
    };

    const getAuctionProgress = (events) => {
        if (props.IsEvents) {
            liveEventIDs.current = events.filter(event => !event.IsPastEvent && !event.IsAuctionTime && !endedLiveEventInfo.current.map(info => info.Id).includes(event.Id)).map(event => event.Id);
            getLiveEventInfo(events);
            //we could swap this out to use SignalR instead of an interval timer:
            if (eventsTimer.current) clearInterval(eventsTimer.current);
            eventsTimer.current = setInterval(() => getLiveEventInfo(events), 60000);
        }
    };

    const addAuctionDLVs = () => {
        if (currentSite?.Site?.IsAuctionSite && (props.IsEventSaleCatalogPage || props.IsDealerInventoryLandingPage || props.IsConsignmentPackageListPage || props.IsStandaloneSale)) {
            let eventId = '';
            let eventName = '';
            let sellerId = '';
            let sellerName = '';
            let userID = currentUser.CRMContactID;
            if (props.IsEventSaleCatalogPage) {
                eventId = searchData.DealerHeaderInfo?.EventID
                eventName = searchData.DealerHeaderInfo?.EventTitle
                sellerId = searchData.DealerHeaderInfo?.ParentAccountCRMID
                sellerName = searchData.DealerHeaderInfo?.Name
            } else if (props.IsConsignmentPackageListPage) {
                eventId = searchData.DealerHeaderInfo?.ParentAuctionGroupID
                eventName = searchData.DealerHeaderInfo?.ParentAuctionGroupName
                sellerId = searchData.DealerHeaderInfo?.DealerCRMID
                sellerName = searchData.DealerHeaderInfo?.Name
            } else if (props.IsStandaloneSale) {
                eventId = searchData.DealerHeaderInfo?.SaleID
                eventName = searchData.DealerHeaderInfo?.Name
                sellerId = searchData.DealerHeaderInfo?.AccountCRMID
                sellerName = searchData.DealerHeaderInfo?.AccountName
            } else if (props.IsDealerInventoryLandingPage) {
                sellerId = searchData.DealerHeaderInfo?.AccountCRMID
                sellerName = searchData.DealerHeaderInfo?.AccountName
            }
            addAuctionSiteDLVs({
                eventId: eventId,
                eventName: eventName,
                sellerId: sellerId,
                sellerName: sellerName,
                userID: userID
            });
        }
    }

    const getLiveEventInfo = (events) => {
        if (liveEventIDs.current.length <= 0) {
            if (eventsTimer.current) clearInterval(eventsTimer.current);
        } else {
            sandhillsRequest({
                url: insertURLParameter(routes.GetLiveEventInfo, "eventIds", liveEventIDs.current.join('|')),
                errorHandling: {
                    message: 'Error retrieving while loading event progress',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                if (response?.length > 0) {
                    endedLiveEventInfo.current.push(...response.filter(event => event.Progress == 100));
                    let updatedListings = [...events].map(listing => {
                        let data = response.find(event => event.Id == listing.Id) || endedLiveEventInfo.current.find(info => info.Id == listing.Id) || {};
                        return {
                            ...listing,
                            ...data
                        };
                    });

                    liveEventIDs.current = liveEventIDs.current.filter(id => !endedLiveEventInfo.current.map(info => info.Id).includes(id));
                    if (props.IsBothEventTypesDealerPage) setLiveListings(updatedListings.filter(listing => !listing.IsPastEvent));

                    dispatchListings({ type: 'update-listings', payload: updatedListings });
                }
            });
        }
    };

    const toggleMobileSort = useCallback(() => {
        togglePushNotifications(showMobileSort);
        toggleScrollToTop(showMobileSort);
        setShowMobileSort(!showMobileSort);
    }, [showMobileSort]);

    const onRequestAQuoteModalToggle = () => {
        setShowRequestAQuoteModal(showRequestAQuoteModal => !showRequestAQuoteModal);
    };

    const mapViewToggle = () => {
        setShowMapViewModal(showMapViewModal => !showMapViewModal);
    };

    useEffect(() => {
        if (locationState && currentSite?.Site?.IsCharterHub) {
            runSearch(false, {
                fieldName: constants.Bounds,
                value: locationState
            });
        }
    }, [locationState]);

    const handlePopState = (e) => {
        var fullPageReload = e?.state?.Timestamp ? false : true;

        if (e?.state?.Timestamp) {
            var timeNow = new Date();
            var diff = timeNow - e.state.Timestamp;
            var hh = (diff / 1000 / 60 / 60);
            fullPageReload = hh >= 2;
        }

        if (fullPageReload) {
            window.location.reload();
        } else if (pushStateToWindowHistory) {
            setIsPopState(true);
            setListingsSearchInProgress(true);
            setCriteriaSearchInProgress(true);
            let result = e.state || props.ListPageAjaxModel;
            let criteriaResult = e.state || props.FacetedSearchAjaxModel;
            setIsFTISearch(e.state?.IsFTISearch || false);
            setIsRentalSearch(e.state?.IsRentalSearch || false);
            setIsFractionalSearch(e.state?.IsFractionalSearch || false);
            setIsOnlineAuctionSearch(e.state?.IsOnlineAuctionSearch || false);
            setIsOnSiteAuctionSearch(e.state?.IsOnSiteAuctionSearch || false);
            handleResult(result);
            handleCriteriaResult(criteriaResult);
            if (!props.IsEvents && !props.IsStolenEquipment && routes.LogListingImpressionsUrl) {
                sandhillsRequest({
                    url: routes.LogListingImpressionsUrl,
                    method: 'post',
                    data: {
                        impressions: result.Listings.filter(listing => listing.LogImpression).map(listing => {
                            return {
                                ListingID: listing.Id,
                                CategoryID: listing.CategoryId,
                                BaseCategoryID: listing.BaseCategoryId
                            }
                        }),
                        requestedPage: window.location.href,
                        isWatchList: props.IsWatchList
                    },
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                });
            }
        }
    };

    const handlePageNumberChange = (page) => {
        setShowLoadingRing(true);
        setIsPopState(false);
        window.scrollTo(0, 0);
        setTimeout(() => {
            setSearchData(previousState => {
                return { ...previousState, CurrentPage: page };
            });
        }, 0);
    };

    const onDistanceLocationModalToggle = () => {
        setIsPopState(false);
        if (windowSize.isMobile || showDistanceModal) {
            setShowDistanceModal(showDistanceModal => !showDistanceModal);
        }
    };

    const adjustElementHeightsByRow = useCallback((className) => {
        const elements = document.querySelectorAll(`.${className}`);
        if (!elements.length) return;

        const itemsPerRow = windowSize.width > 1251 ? 4 : windowSize.width > 1000 ? 2 : 1;
        const rows = [];
        let currentRow = [];

        const elementData = Array.from(elements).map((element, index) => {
            element.style.height = "";

            currentRow.push(element);

            if ((index + 1) % itemsPerRow === 0 || index === elements.length - 1) {
                rows.push([...currentRow]);
                currentRow = [];
            }

            return {
                element,
                height: element.getBoundingClientRect().height
            };
        });

        requestAnimationFrame(() => {
            let elementIndex = 0;

            rows.forEach(row => {
                const rowElements = elementData.slice(elementIndex, elementIndex + row.length);
                const maxHeight = Math.max(...rowElements.map(data => data.height));

                rowElements.forEach(data => {
                    data.element.style.height = `${maxHeight}px`;
                });

                elementIndex += row.length;
            });
        });
    }, [windowSize.width]);

    const onDistanceLocationModalTouch = () => {
        setIsPopState(false);
        setShowDistanceModal(showDistanceModal => !showDistanceModal);
    }

    const onDistanceLocationModalUpdate = useCallback((location) => {
        setIsPopState(false);
        let pendingSearchCriteria = [
        ];
        if (!props.IsHostedSite) {
            setIsDistanceModalSearch(true);
            setUseCurrentLocation(location?.UseCurrentLocation ?? false);
            pendingSearchCriteria.push({ fieldName: constants.City, value: '' });
        }
        setCriteriaData(previousState => {
            return {
                ...previousState,
                PendingSearchCriteria: [
                    ...previousState.PendingSearchCriteria,
                    ...pendingSearchCriteria
                ],
                SelectedSortValue: searchData.distanceSortID,
            };
        });
        setShowDistanceModal(false);
    }, [props.IsHostedSite, searchData.distanceSortID]);

    const onSortChange = useCallback((sortID) => {
        setIsPopState(false);
        setUseCurrentLocation(false);

        if (sortID === searchData.distanceSortID && ((criteriaData.SelectedSortValue !== searchData.distanceSortID) || props.RequestLocationOnMount)) {
            setIsLoading(true);
            setShowLoadingRing(true);
            setDisplaySCFInput(true);
            setTimeout(() => {
                runSortWithCurrentLocation_CookiesFirst(
                    routes.FormattedLocationStringFromCurrentCoordinates,
                    routes.AjaxGetCoordinatesFromLocationStringUrl,
                    currentUser.CurrentLocation?.CurrentAuthenticatedUserLocationForCoordinates,
                    routes.AjaxSetCurrentUserLocationCookie,
                    routes.AjaxGetDecryptedCoordinatesFromCurrentLocationCookie,
                    true
                ).then(location => {
                    unsetScrollPosition();
                    distanceSortError(false);
                    onDistanceLocationModalUpdate();
                    setCurrentUser(user => {
                        return {
                            ...user,
                            CurrentLocation: {
                                ...user.CurrentLocation,
                                FormattedAddress: location.formattedAddress
                            }
                        };
                    });
                    setIsLoading(false);
                }).catch(() => {
                    if (!searchData.DisplayShowClosestFirstByConsent) {
                        setShowDistanceModal(showDistanceModal => !showDistanceModal);
                    } else {
                        distanceSortError(true);
                    }
                    setIsLoading(false);
                    setShowLoadingRing(false);
                });
            }, 0)
            setShowLoadingRing(false);
        } else {
            setShowLoadingRing(true);
            let pendingSearchCriteria = [];
            if (props.IsHostedSite && criteriaData.SearchPageURL.includes(constants.IsSegmentSearch) && !criteriaData.SearchPageURL.includes(constants.InventoryType) && criteriaData.SearchPageURL.includes(constants.ScopeCategoryIDs)) {
                pendingSearchCriteria.push({ fieldName: constants.IsSegmentSearch, value: '' });
            }
            if (!props.IsHostedSite && criteriaData.SelectedSortValue === searchData.distanceSortID) {
                let state = getURLParameter(criteriaData.SearchPageURL, constants.State).split('|').filter(s => s);
                setIsDistanceModalSearch(false);
                pendingSearchCriteria.push({ fieldName: constants.City, value: '' });

                if (state.length == 1 && props.DefaultStateLocations?.find(s => s.toUpperCase() == state[0])) {
                    pendingSearchCriteria.push({ fieldName: constants.State, value: '' });
                }
            }
            if (criteriaData.ClassificationToForceScope) {
                pendingSearchCriteria.push({ fieldName: constants.ScopeCategoryIDs, value: criteriaData.ClassificationToForceScope });
            }
            setTimeout(() => {
                setCriteriaData(previousState => {
                    return {
                        ...previousState,
                        PendingSearchCriteria: [
                            ...previousState.PendingSearchCriteria,
                            ...pendingSearchCriteria
                        ],
                        SelectedSortValue: sortID
                    };
                });
            }, 0);
            setShowLoadingRing(false);
        }
    }, [
        searchData.distanceSortID,
        criteriaData.SelectedSortValue,
        criteriaData.SearchPageURL,
        criteriaData.ClassificationToForceScope,
        props.IsHostedSite,
        props.DefaultStateLocations,
        currentUser.CurrentLocation?.CurrentAuthenticatedUserLocationForCoordinates,
    ]);

    const distanceSortError = useCallback((showError) => {
        setShowDistanceSortError(showError);
        setShowDistanceModal(showError);
    }, []);

    const onResultsChange = (selectedValue) => {
        setIsPopState(false);
        setSearchData(previousState => {
            return { ...previousState, PageSize: selectedValue };
        });
    };

    const toggleMobileFilter = useCallback((skipSearch) => {
        let newShowMobileFilters = !showMobileFilters;
        setShowMobileFilters(newShowMobileFilters);
        if (newShowMobileFilters) {
            setMobileSearch(window.innerWidth <= 785);
            setTimeout(() => {
                setIOSScrollLock(windowSize.isMobile);
            }, 750);
        } else {
            setTimeout(() => {
                removeIOSScrollLock(windowSize.isMobile);
            }, 750);
            setMobileSearch(false);
            if (!skipSearch) {
                runSearch(false);
            }
        }
    }, [showMobileFilters, windowSize.isMobile, runSearch]);

    const clearAll = useCallback(() => {
        window.scrollTo(0, 0);
        makeSearchCalls(criteriaData.ClearAllURL, false);
    }, [criteriaData.ClearAllURL]);

    const clearFacet = useCallback((fieldName, option) => {
        let searchUrl = buildURL(false, [], fieldName);
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = criteriaData.SelectedFacets.filter(f => f.FieldName == fieldName)[0];

        if (option && option.IsGroup && (selectedField.ParentNameForSearch && selectedField.ParentNameForSearch != '')) {
            queryStringParamName = selectedField.ParentNameForSearch;
        }

        if (fieldName == constants.Distance) {
            searchUrl = removeURLParameter(searchUrl, constants.Distance);
            searchUrl = removeURLParameter(searchUrl, constants.CityID);
            searchUrl = removeURLParameter(searchUrl, constants.AirportID);

            //reset default map
            handleSetMapCenter(40.8058, -96.6718, 0);
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            if (option.ReactKey == constants.OtherItems) {
                searchUrl = removeURLParameter(searchUrl, constants.ScopeCategoryIDs);
            }

            if (currentSite.Site.IsAuctionTime || currentSite.Site.IsBidcaller) {
                if (fieldName === constants.Animal || fieldName === constants.Crop) {
                    searchUrl = removeURLParameter(searchUrl, constants.ClassificationCategory);

                }
            }

            let index = qsValues.map(value => value.toLowerCase()).indexOf(option.Value?.toLowerCase?.() || option.toLowerCase?.());
            if (index > -1) {
                qsValues.splice(index, 1);
            }

            let queryString = qsValues.join('|');
            if (queryString.length > 0) {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, queryString);
            } else if (props.ClearAllClearScopeCat) {
                searchUrl = removeURLParameter(searchUrl, constants.ScopeCategoryIDs);
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        if ((shouldClearDistanceSort(searchUrl) || (props.ShowCityInternalLinksByDefault && fieldName == constants.City)) && criteriaData.SelectedSortValue == searchData.distanceSortID) {
            searchUrl = removeURLParameter(searchUrl, constants.Sort);
        }
        if (queryStringParamName == constants.State && getURLParameter(searchUrl, constants.FilterByState) != '' && getURLParameter(searchUrl, queryStringParamName) == '') {
            searchUrl = removeURLParameter(searchUrl, constants.FilterByState);
        }
        if (queryStringParamName == constants.WholeSaleOptions && option == constants.PreATResults) {
            searchUrl = removeURLParameter(searchUrl, constants.PreATSoldUTCDateTime);
        }
        if (criteriaData.ClassificationToForceScope) {
            searchUrl = insertURLParameter(searchUrl, constants.ScopeCategoryIDs, criteriaData.ClassificationToForceScope);
        }

        makeSearchCalls(searchUrl, false);
    }, [
        criteriaData.SelectedFacets,
        criteriaData.SelectedSortValue,
        criteriaData.ClassificationToForceScope,
        props.ClearAllClearScopeCat,
        props.ShowCityInternalLinksByDefault,
        searchData.distanceSortID,
    ]);

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    const shouldClearDistanceSort = (searchUrl) => {
        let cities = getURLParameter(searchUrl, constants.City).split('|').filter(c => c) ?? [];
        let states = getURLParameter(searchUrl, constants.State).split('|').filter(s => s) ?? [];
        let clearDistanceSort = !isDistanceModalSearch;

        if ((searchUrl.includes(constants.State) || searchUrl.includes(constants.City)) && criteriaData.SelectedSortValue == searchData.distanceSortID && !props.IsDistanceSortForStateAndCitySearches) {
            clearDistanceSort = cities.length != 1 || states.length != 1;
        }

        return clearDistanceSort;
    };

    const runSearch = useCallback((keepPage, latestFieldValue, nextFacetToLoad, searchCriteria, isFTIToggle) => {
        let pendingChanges = [...criteriaData.PendingSearchCriteria];
        if (pendingChanges.length < 1 && searchCriteria) {
            pendingChanges = searchCriteria;
        }
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        // Scroll to top always on mobile searches:
        if (window.innerWidth <= 785) {
            window.scrollTo(0, 0);
        }

        makeSearchCalls(buildURL(keepPage, pendingChanges, null, isFTIToggle), nextFacetToLoad);
    }, [
        criteriaData.PendingSearchCriteria,
        criteriaData.SelectedSortValue,
        criteriaData.SearchBaseURL,
        searchData.CurrentPage,
        searchData.PageSize,
        isFTISearch,
        isDistanceModalSearch
    ]);

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName === pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...criteriaData.SearchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];
            if (latestFieldValue.fieldName === field.FieldName && field.Type === 'Range') {
                // update the range field in state so it doesn't do a weird re-render with the old value:
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setCriteriaData(previousState => {
                    return { ...previousState, SearchFields: fields };
                });
                break;
            }
        }

        return pendingChanges;
    };

    const buildURL = (keepPage, pendingChanges, fieldName, isFTIToggle) => {
        let searchUrl = criteriaData.SearchBaseURL;
        if ((fieldName === constants.InventoryType || pendingChanges?.some(c => c.fieldName === constants.InventoryType)) && !isFTIToggle) {
            let addon = criteriaData.SelectedFacets?.some(c => c.FieldName === 'WLID') ? '?WLID=' + criteriaData.SelectedFacets.find(c => c.FieldName === 'WLID').Options[0].Value : '';
            searchUrl = criteriaData.SegmentBaseUrl + addon;
        }

        let state = getURLParameter(searchUrl, constants.State).split('|').filter(s => s);
        let checkSCF = false;

        if (isDistanceModalSearch && state.length == 1 && props.DefaultStateLocations?.find(s => s.toUpperCase() == state[0])) {
            pendingChanges.push({ fieldName: constants.State, value: '' });
        }
        if (criteriaData.ClassificationToForceScope) {
            searchUrl = insertURLParameter(searchUrl, constants.ScopeCategoryIDs, criteriaData.ClassificationToForceScope);
        }

        pendingChanges = pendingChanges.filter(obj => !(obj.fieldName === constants.EventType && obj.value === ''));

        if (pendingChanges.some(x => rentalFacetOptions.some(c => x.fieldName == c)) || pendingChanges.some(x => leaseFacetOptions.some(c => x.fieldName == c))) {
            if (rentalFacetOptions.filter(c => searchUrl.includes(c)).length > 0) {
                let options = rentalFacetOptions.filter(c => searchUrl.includes(c));
                options.forEach(obj => {
                    searchUrl = removeURLParameter(searchUrl, obj);
                });
            } else if (leaseFacetOptions.filter(c => searchUrl.includes(c)).length > 0) {
                let options = leaseFacetOptions.filter(c => searchUrl.includes(c));
                options.forEach(obj => {
                    searchUrl = removeURLParameter(searchUrl, obj);
                });
            }
        }

        pendingChanges.forEach(change => {
            if (!change.IsSpecGroup) {
                searchUrl = removeURLParameter(searchUrl, change.fieldName);
                if (change.value && (change.value.length > 0 || !isNaN(change.value))) {
                    searchUrl = insertParameterInToURL(searchUrl, change);

                    if (!props.ShowCityInternalLinksByDefault && change.fieldName == constants.State && searchUrl.includes(constants.City)) {
                        searchUrl = removeURLParameter(searchUrl, constants.City);
                        checkSCF = true;
                    }

                    if (change.fieldName == constants.Country && searchUrl.includes(constants.State)) {
                        searchUrl = removeURLParameter(searchUrl, constants.State);
                        checkSCF = true;
                    }

                    if (change.fieldName == constants.WholeSaleOptions && change.value == constants.PreATResults) {
                        searchUrl = insertURLParameter(searchUrl, constants.FleetEvaluatorSortValue, constants.AllListings );
                    }
                }
                else if (change.fieldName == constants.AirportDetails) {
                    searchUrl = removeURLParameter(searchUrl, constants.Distance);
                    searchUrl = removeURLParameter(searchUrl, constants.CityID);
                    searchUrl = removeURLParameter(searchUrl, constants.AirportID);

                    if (change.airport?.AirportCodeID > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.AirportID, change.airport.AirportCodeID);
                    }
                    if (change.airport?.AirportCityID > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.CityID, change.airport.AirportCityID);
                    }
                    if (change.distance && change.distance > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.Distance, change.distance);
                    }

                    if (!isNaN(change.airport.lat) && !isNaN(change.airport.lng)) {
                        handleSetMapCenter(change.airport.lat, change.airport.lng, change.distance);
                    }
                }
                else if (change.fieldName == constants.Bounds) {
                    if (change.value) {
                        Object.keys(change.value).forEach(key => {
                            searchUrl = insertURLParameter(searchUrl, key, change.value[key]);
                        });
                    }
                }
            } else {
                if (change.AllAvailableOptions && change.AllAvailableOptions.length > 0) {
                    change.AllAvailableOptions.forEach((fieldName) => {
                        searchUrl = removeURLParameter(searchUrl, fieldName);
                    });
                }

                if (change.value && (change.value.length > 0 || !isNaN(change.value))) {
                    searchUrl = insertURLParameter(searchUrl, change.fieldName, change.value, false).replace(constants.FacetedSpecGroup, '&');
                }
            }
        });

        searchUrl = removeURLParameter(searchUrl, constants.Sort);
        searchUrl = removeURLParameter(searchUrl, constants.UseCurrentLocation);
        state = getURLParameter(searchUrl, constants.State).split('|').filter(s => s);

        if (state.length == 1 && props.DefaultStateLocations?.find(s => s.toUpperCase() == state[0]) && !searchUrl.includes(constants.Country)) {
            setIsDistanceModalSearch(false);
            checkSCF = true;
        }

        let isDistanceSort = criteriaData.SelectedSortValue == searchData.distanceSortID;
        let isValidSortValue = isDistanceSort && checkSCF ? !shouldClearDistanceSort(searchUrl) : true;
        const wsoOption = pendingChanges.find(x => x.fieldName === 'wso');
        const isPatrOption = wsoOption?.value?.includes('patr');

        if (isValidSortValue) {
            const selectedSortValue = wsoOption ? (isPatrOption ? 23 : 22) : criteriaData.SelectedSortValue;
            searchUrl = insertURLParameter(searchUrl, constants.Sort, selectedSortValue);

            if (isDistanceSort && useCurrentLocation) {
                searchUrl = insertURLParameter(searchUrl, constants.UseCurrentLocation, useCurrentLocation)
            }
        }
        if (!isFTISearch && searchUrl.includes('fti')) {
            //use default best wholesale price sort
            searchUrl = insertURLParameter(searchUrl, constants.Sort, 22);
        }

        searchUrl = removeURLParameter(searchUrl, constants.Page);
        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, constants.Page, searchData.CurrentPage);
        }

        searchUrl = removeURLParameter(searchUrl, constants.PageSize);
        if (showNumResultsDisplayedSelect && searchData.PageSize != (props.UseNewListPageDesign ? defaultNewListPageSize : defaultListPageSize)) {
            searchUrl = insertURLParameter(searchUrl, constants.PageSize, searchData.PageSize);
        }

        return searchUrl;
    };

    const insertParameterInToURL = (searchUrl, change) => {
        if (change.fieldName === constants.ListingType) {
            if (change.value === constants.AuctionResults) {
                searchUrl = searchUrl.replace(constants.ActivationUTCDateTime, constants.SoldUTCDateTime);

                if (currentSite?.Site?.IsAuctionTime && !searchUrl.includes('EventCategoryID=8')) {
                    searchUrl = insertURLParameter(searchUrl, constants.EventCategoryID, '7');
                }
            } else {
                searchUrl = searchUrl.replace(constants.SoldUTCDateTime, constants.ActivationUTCDateTime);
            }
        } else if (change.fieldName === constants.WholeSaleOptions) {
            if (change.value !== constants.PreATResults) {
                searchUrl = searchUrl.replace(constants.PreATSoldUTCDateTime, constants.ActivationUTCDateTime);
            }
        }

        searchUrl = insertURLParameter(searchUrl, change.fieldName, change.value);
        return searchUrl;
    };

    const makeSearchCalls = (listingSearchUrl, nextFacetToLoad) => {
        let criteriaSearchUrl = listingSearchUrl.replace(props.AjaxListingsSearchRouteValue, props.AjaxCriteriaSearchRouteValue);
        if (CardType !== 'GRID' && CardType == 'WEBLISTING') {
            listingSearchUrl = insertURLParameter(listingSearchUrl, 'forceImages', '1');
        }
        setToggleCloseMA(false);
        if (!nextFacetToLoad || (nextFacetToLoad === 'ListingAjaxSearch')) {
            setIsPopState(false);
            setListingsSearchInProgress(true);
            sandhillsRequest({
                url: listingSearchUrl,
                errorHandling: {
                    useConsole: true,
                    useAlert: false,
                    shouldRefreshPage: true
                }
            }).then((data) => {
                handleResult(data);
            }).catch((error) => {
                setListingsSearchInProgress(false);
                setShowLoadingRing(false);

                if (!alertModalStatus?.show) {
                    setAlertModalStatus({
                        show: true,
                        message: error.message,
                        title: error.title,
                        shouldRefreshPageOnClose: error.shouldRefreshPage,
                        containerClassName: error.isSecurityChallenge ? 'security-challenge-modal' : ''
                    });
                }
            });
        }
        fetchMapData(listingSearchUrl);

        if ((nextFacetToLoad === 'ListingAjaxSearch') ? false : true) {
            setCriteriaSearchInProgress(true);
            sandhillsRequest({
                url: criteriaSearchUrl,
                errorHandling: {
                    useConsole: true,
                    useAlert: false,
                    shouldRefreshPage: true
                }
            }).then((data) => {
                handleCriteriaResult(data, nextFacetToLoad, listingSearchUrl);
            }).catch((error) => {
                setCriteriaSearchInProgress(false);
                setShowLoadingRing(false);

                if (!alertModalStatus?.show) {
                    setAlertModalStatus({
                        show: true,
                        message: error.message,
                        title: error.title,
                        shouldRefreshPageOnClose: error.shouldRefreshPage,
                        containerClassName: error.isSecurityChallenge ? 'security-challenge-modal' : ''
                    });
                }
            });
        }
    };

    const handleCriteriaResult = (data, nextFacetToLoad, searchUrl) => {
        let searchPageUrl = data.SearchPageURL;
        if (searchPageUrl && searchPageUrl.startsWith('http://') && window.location.origin.startsWith('https://')) {
            searchPageUrl = searchPageUrl.replace('http://', 'https://');
        }

        let eventID = parseInt(getURLParameter(searchPageUrl, constants.EventID));
        if (!eventID || eventID < 0) {
            setIsEventSaleCatalogPage(false);
        }

        if (!criteriaData.SelectedSortValue || criteriaData.SelectedSortValue == searchData.defaultSortID) {
            searchPageUrl = removeURLParameter(searchPageUrl, constants.Sort);
        }

        if (nextFacetToLoad) {
            var facetToLoad = data.SearchFields.filter(f => f.FieldName == nextFacetToLoad)[0];
            if (facetToLoad) {
                facetToLoad.IsNextUp = true;
            } else {
                setAlertModalStatus({ show: true, message: translator.translate('sandhill.noadditionalfiltercriteriaavailable') });
                makeSearchCalls(searchUrl, 'ListingAjaxSearch');
            }
        }

        setCriteriaData({
            MetaTitle: data.MetaTitle,
            PageHeader: data.PageHeader,
            InternalLinkingModel: data.InternalLinkingModel,
            ClearAllURL: data.ClearAllURL,
            SegmentBaseUrl: data.SegmentBaseUrl,
            BreadCrumbs: data.BreadCrumbs,
            SearchFields: data.SearchFields,
            SelectedFacets: data.SelectedFacets,
            ConflictingSpecs: data.ConflictingSpecs,
            ConflictingFacets: data.ConflictingFacets,
            PendingSearchCriteria: [],
            SelectedSortValue: data.SelectedSortValue,
            SearchBaseURL: data.AjaxSearchURL || data.SearchBaseURL,
            SearchPageURL: searchPageUrl,
            SAMModel: data.SAMModel,
            IsDefaultSearch: data.IsDefaultSearch,
            LoginLink: data.LoginLink,
            ClassificationToForceScope: data.ClassificationToForceScope,
            TurnOffDefaultSaveThisSearch: data.TurnOffDefaultSaveThisSearch,
            IsAttachmentAjaxSearch: data.IsAttachmentAjaxSearch,
            IsAuctionResults: data.IsAuctionResults,
            RelevantPartsLink: data.RelevantPartsLink,
            RelevantPartsTitle: data.RelevantPartsTitle,
            PriceBlurb: data.PriceBlurbs,
            KeywordSearchParamsDetected: data.KeywordSearchParamsDetected,
            IsRentalSearch: isRentalSearch,
            IsFractionalSearch: isFractionalSearch,
            IsOnlineAuctionSearch: isOnlineAuctionSearch,
            IsOnSiteAuctionSearch: isOnSiteAuctionSearch,
            IsOtherItemsPage: data.IsOtherItemsPage
        });

        setShowVIPDisclaimer(data.ShowVIPDisclaimer);

        setDisplaySCFInput(data.SelectedSortValue == searchData.distanceSortID);
        if (data.MetaTitle) {
            document.title = data.MetaTitle;
        }

        setCriteriaSearchInProgress(false);
    };

    const getListingAuctionData = useCallback((listings, dispatchListings) => {
        if (listings?.length > 0) {
            const atListings = getListingAuctionData_ATModels(listings);
            const efListings = getListingAuctionData_EFModels(listings);

            if (atListings?.length > 0 || efListings?.length > 0) {
                sandhillsRequest({
                    url: routes.GetAuctionData,
                    method: 'post',
                    data: {
                        search: {
                            AuctionTimeModels: atListings,
                            EquipmentFactsModels: efListings
                        }
                    },
                    errorHandling: {
                        message: 'Error retrieving auction data',
                        showDetails: true,
                        useConsole: true,
                        useAlert: false
                    }
                }).then((response) => {
                    const data = response;

                    if (data) {                        
                        let biddingDataResults = [];
                        if (data.AuctionTimeResult?.length > 0) {
                            biddingDataResults.push(...data.AuctionTimeResult);
                            // Since this will not be saved by the reducer, we need to store this in a Ref we can reference later from real time updates
                            latestAuctionBiddingData.current = [...data.AuctionTimeResult]
                            if (props.AuctionTimeBiddingHubUrl.trim() !== "" && latestAuctionBiddingData.current && latestAuctionBiddingData.current.length > 0) {
                                let eventIds = latestAuctionBiddingData.current.map(l => l.AuctionUpdateKey).join(",");
                                if (!initializedATSignalR.current) {
                                    initializedATSignalR.current = setupAuctionTimeBiddingSignalRHub(props.AuctionTimeBiddingHubUrl, props.ShowEmployeeOnlyInfo, currentSite?.IsMobileAppContext, eventIds, updateLatestBiddingData, updateBiddingPausedStatus, currentUser.UserAuthID);                                    
                                    //Subscribe to SignalR connection status
                                    subscribeToSignalRNotConnectedFallback(signalRNotConnectedFallback);                                   
                                }
                                else {
                                    changeWatchedAuctionGroups(eventIds);
                                }
                            }
                        }
                        if (data.EquipmentFactsResult?.length > 0) biddingDataResults.push(...data.EquipmentFactsResult)

                        if (biddingDataResults.length > 0) {
                            dispatchListings({
                                type: 'update-auction-bidding-data',
                                payload: biddingDataResults
                            });                            
                        }
                    }
                });
            }
        }
    }, [
        getListingAuctionData_ATModels,
        getListingAuctionData_EFModels,
        props.AuctionTimeBiddingHubUrl,
        props.ShowEmployeeOnlyInfo,
        currentSite?.IsMobileAppContext,
        updateLatestBiddingData,
        updateBiddingPausedStatus,
        currentUser.UserAuthID
    ]);

    const getListingAuctionData_ATModels = (listings) => {
        const atListings = listings.filter(listing => listing && listing.IsAT === true && !listing.IsAuctionResult);

        return (atListings?.length > 0) ?
            atListings.map(m => ({
                LegacyBaseCatID: m.LegacyBaseCategoryId,
                BaseCatID: m.BaseCategoryId,
                ListingID: m.Id,
                AverageWeight: m.AverageWeight,
                Quantity: m.Quantity,
                NextBidAmount: m.NextBidAmount,
                IsAuctionLive: m.IsAuctionLive,
                EventID: m.EventID
            })) : [];
    };

    const getListingAuctionData_EFModels = (listings) => {
        const efListings = listings.filter(listing => listing && listing.IsEF === true && !listing.IsAuctionResult);

        return (efListings?.length > 0) ?
            efListings.map(m => ({
                LegacyBaseCatID: m.LegacyBaseCategoryId,
                BaseCatID: m.BaseCategoryId,
                ListingID: m.Id,
                CurrencyCode: m.CurrencyCode,
                EventID: m.EventID,
                ListingTitle: m.ListingTitle,
                ListingSerialNumber: m.SerialNumber,
                AuctionPriceLabel: m.AuctionPriceLabel,
                Quantity: m.Quantity,
                NextBidAmount: m.NextBidAmount
            })) : [];
    };

    const getListingBiddingDataForListingsByKey = (keys) => {
        if (keys?.length > 0) {
            return latestAuctionBiddingData.current.filter(l => keys.includes(l.AuctionUpdateKey));
        } else {
            return [];  
        }
    };

    // Be mindful that because this is a function executed from a timeout, that utilizing a stateful value may not be current
    const fetchAuctionData = () => {
        if (AuctionInformationUpdateKeys.current?.length > 0) {            
            sandhillsRequest({
                url: insertURLParameter(routes.AuctionUpdatesGetUpdate,'last', formatDateToDateAndTimeWithSeconds(lastAuctionUpdatedReceived.current)) + '&' + getBidStatusBannerQueryString(getListingBiddingDataForListingsByKey(AuctionInformationUpdateKeys.current)),
                method: 'POST',
                data: {
                    Keys: AuctionInformationUpdateKeys.current
                },
                errorHandling: {
                    message: 'Error retrieving auction data',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then(response => {
                const data = response;
                // Note - AuctionUpdates does not return an array when BiddingInputPaused is true
                if (data?.AuctionUpdates?.length > 0 || data.BiddingInputPaused) {
                    auctionInformationErrorCount = 0; // Reset error count on successful response
                    auctionInformationEmptyResponseCount = 0;

                    lastAuctionUpdatedReceived.current = new Date(); // Save last update value for next request
                    updateLatestBiddingData(data?.AuctionUpdates, data.BiddingInputPaused);                    
                }
                else if (response === null || response === "") {
                    auctionInformationErrorCount++;
                    logAuctionMessage("Countdown Error count = ", auctionInformationErrorCount);
                }
                else if (data?.AuctionUpdates?.length === 0 && !data.BiddingInputPaused) {
                    auctionInformationEmptyResponseCount++;
                    logAuctionMessage("Countdown Empty Response count = ", auctionInformationEmptyResponseCount);
                    if (auctionInformationEmptyResponseCount >= 12) {
                        let currentURL = window.location.href;
                        let reloadCount = parseInt(getURLParameter(currentURL, queryString.EmptyResponse), 10) || 0;
                        reloadCount += 1;
                        window.location.href = insertURLParameter(currentURL, queryString.EmptyResponse, reloadCount); // After 1 minute of empty responses, refresh the page
                    }
                }
            }).catch(error => {
                auctionInformationErrorCount++;
                logAuctionMessage(`Error updating listings:`, error);
            }).finally(() => {
                if (auctionInformationErrorCount >= 2) {
                    let currentURL = window.location.href;
                    let reloadCount = parseInt(getURLParameter(currentURL, queryString.InfoRequestFailure), 10) || 0;
                    reloadCount += 1;
                    window.location.href = insertURLParameter(currentURL, queryString.InfoRequestFailure, reloadCount); // Refresh the page if there are 2 consecutive errors
                }
                else if (AuctionInformationUpdateKeys.current.length > 0) {
                    auctionInformationTimeout.current = setTimeout(fetchAuctionData, 5000);
                }
                else {
                    clearAuctionInformationTimeout();
                }
            });
        }
    };

    const updateBiddingPausedStatus = (biddingInputPaused) => {
        // Clearing the timeout so that we have a bit more control over the timing of the updates
        // Since the status just changed we may get fresh data from the server instead of the simple Update Information
        clearTimeout(auctionInformationTimeout.current);
        // Patch in the update to the data
        updateLatestBiddingData(null, biddingInputPaused);
        // Reset the timeout to check again in 5 seconds, if necessary
        if (AuctionInformationUpdateKeys.current.length > 0) {
            auctionInformationTimeout.current = setTimeout(fetchAuctionData, 5000);
        }
        else {
            clearAuctionInformationTimeout();
        }
    }

    const updateLatestBiddingData = (auctionUpdateDataArray, biddingInputPaused) => {
        // Data that needs a fresh pull from the server
        const updateListingBiddingData = [];
        // Data that will be pushed to our ref property
        const updatedLatestAuctionBiddingData = [];
        // Data to be pushed to the Dispatch with any updated data
        const updatedBiddingData = []

        latestAuctionBiddingData.current.forEach(currentAuctionBiddingData => {
            // SignalR may not provide the array if only the bid status changed, but attempt to find it
            const auctionUpdateData = auctionUpdateDataArray?.find(x => x.EventIDPub == currentAuctionBiddingData.AuctionUpdateKey);
            const allBiddingPausedChanged = biddingInputPaused != null && currentAuctionBiddingData.IsAllBiddingPaused != biddingInputPaused

            // If we found information, or a bidding pause status was provided we'll want to make sure we update the objects
            if (auctionUpdateData || allBiddingPausedChanged) {
                // Patch in the updated data only if we get a match for both
                const updatedData = patchInUpdatedAuctionData(routes,currentAuctionBiddingData, auctionUpdateData, biddingInputPaused);
                updatedLatestAuctionBiddingData.push(updatedData);
                updatedBiddingData.push(updatedData);

                // Only consider the paused status as changed if it is turning back on. Then we'll mark it so we can pull fresh data
                const listingBiddingPausedChanged = currentAuctionBiddingData.IsListingPaused != updatedData.ListingIsPaused && updatedData.ListingIsPaused == false;
                if ((allBiddingPausedChanged && biddingInputPaused == false) || listingBiddingPausedChanged) {
                    let listing = listings.find(l => l.EventID == updatedData.EventID);
                    if (listing) {
                        updateListingBiddingData.push(listing)
                    }
                }
            }
            else {
                // Otherwise push to the updated list as is. This will be for our ref object
                updatedLatestAuctionBiddingData.push(currentAuctionBiddingData);
            }
        });

        dispatchListings({
            type: 'update-auction-bidding-data',
            payload: updatedBiddingData
        });

        latestAuctionBiddingData.current = updatedLatestAuctionBiddingData;

        if (updateListingBiddingData.length > 0) {
            getListingAuctionData(updateListingBiddingData, dispatchListings);
        }
    }

    const clearAuctionInformationTimeout = () => {
        if (auctionInformationTimeout.current) {
            // Clear the timeout and reset the running flag so we know that we're not already running
            logAuctionMessage("Stopped auction information updates");
            clearTimeout(auctionInformationTimeout.current)
            auctionInformationTimeout.current = 0;
        }
    }

    useEffect(() => {             
        AuctionInformationUpdateKeys.current = countdownListings.map(m => m?.AuctionBiddingData?.AuctionUpdateKey).filter(Boolean);
        logAuctionMessage("countdownListings changed", AuctionInformationUpdateKeys.current);

        if (countdownListings.length > 0) {
            // Ensures that only 1 timeout is running
            if (!auctionInformationTimeout.current) {
                logAuctionMessage("Starting auction information updates");
                // Make an AJAX call every 5 seconds                
                auctionInformationTimeout.current = setTimeout(fetchAuctionData, 5000);
            }
        }
        else {
            clearAuctionInformationTimeout();
        }
    }, [countdownListings]);

    // Don't memoize this function as it uses listings from state
    const resetAuctionDataForListing = (listingID, addedToWatchlist, bidResult, skipListingDataLookup, bidIncrementUpdate, auctionUpdateKey) => {
        if (bidResult && auctionUpdateKey) {
            //This should be a bid result from AT bid submission
            const index = latestAuctionBiddingData.current.findIndex(
                x => x.AuctionUpdateKey === auctionUpdateKey
            );

            if (index >= 0) {                     
                //Need to copy the existing bidding data and just update the user bid data.
                const updatedBiddingData = bidIncrementUpdate ? { ...latestAuctionBiddingData.current[index], UserBidInfo: bidResult, AuctionUpdateCounter: bidResult.AuctionUpdateCounter, Increment: bidIncrementUpdate } : { ...latestAuctionBiddingData.current[index], UserBidInfo: bidResult, AuctionUpdateCounter: bidResult.AuctionUpdateCounter };                                             

                const updatedLatestAuctionBiddingData = [];                

                latestAuctionBiddingData.current.forEach(currentAuctionBiddingData => {                    
                    // If we found information, or a bidding pause status was provided we'll want to make sure we update the objects
                    if (auctionUpdateKey == currentAuctionBiddingData.AuctionUpdateKey) {
                        // Use updated bidding data
                        updatedLatestAuctionBiddingData.push(updatedBiddingData);                        
                    }
                    else {
                        // Otherwise push to the updated list as is. This will be for our ref object
                        updatedLatestAuctionBiddingData.push(currentAuctionBiddingData);
                    }
                });

                //Should just need to update single listing user bid on
                dispatchListings({
                    type: 'update-auction-bidding-data',
                    payload: [updatedBiddingData]
                });

                //Update by ref list of auction bidding data on page.
                latestAuctionBiddingData.current = updatedLatestAuctionBiddingData;                
            };
         } else if (listingID) {
            let listing = listings.find(l => l.Id == listingID);
            if (listing) {
                if (!skipListingDataLookup || skipListingDataLookup == false) {
                    getListingAuctionData([listing], dispatchListings);
                }
                if (addedToWatchlist && userWatchlist.indexOf(listingID) == -1)
                    setUserWatchlist(userWatchlist.concat([listing]));
            }
        }
    }

    const fetchMapData = (searchUrl) => {
        if (!props.ListingsMapDataUrl) return;
        setMapDataLoading(true);
        const queryString = searchUrl && searchUrl.indexOf('?') > -1 ? searchUrl.substring(searchUrl.indexOf('?')) : '';
        const mapDataUrl = props.ListingsMapDataUrl + queryString;

        sandhillsRequest({
            url: mapDataUrl,
        }).then((data) => {
            setSearchData(prev => ({
                ...prev,
                ListingLocations: data.ListingLocations || [],
                ListingMapData: data.ListingMapData || [],
                DefaultCenter: data.DefaultCenter,
                DefaultZoom: data.DefaultZoom,
            }));
            setMapDataLoading(false);
        }).catch(() => {
            setMapDataLoading(false);
        });
    };

    const handleResult = (data) => {
        dispatchListings({
            type: 'update-listings',
            payload: data.Listings
        });

        if (props.IsBothEventTypesDealerPage) {
            setPastListings(data.Listings.filter(listing => listing.IsPastEvent));
            setLiveListings(data.Listings.filter(listing => !listing.IsPastEvent));
        }

        if (!data.SkipAuctionDataGetToDetermineBidStatus) {
            setCountdownListings([]); // Reset the countdown listings so we can rebuild it for a new page
            getListingAuctionData(data.Listings, dispatchListings);
        }

        setSimilarListings({
            type: 'update-listings',
            payload: data.SimilarListingsModel?.SimilarListings || []
        });

        if (data.SimilarListingsModel?.SimilarListings?.length > 0) {
            getListingAuctionData(data.SimilarListingsModel.SimilarListings, setSimilarListings);
        }

        setSearchData(prev => ({
            DoubleClickAdCollectionViewModel: data.DoubleClickAdCollectionViewModel,
            ListingCountTitle: data.ListingCountTitle,
            SortOrderItems: data.SortOrderItems,
            distanceSortID: data.distanceSortID,
            defaultSortID: data.defaultSortID,
            CurrentPage: data.CurrentPage,
            DisplayShowClosestFirstByConsent: data.DisplayShowClosestFirstByConsent,
            PageSize: data.PageSize,
            TotalPages: data.TotalPages,
            ListingsCount: data.ListingsCount,
            LocalizedSimilarListingsInfo: data.LocalizedSimilarListingsInfo,
            ShowBlurb: data.ShowBlurb,
            HighLowAverageURL: data.HighLowAverageURL,
            ShippingEstimates: [],
            CategoryInformation: data.CategoryInformation,
            SimilarListingsModel: data.SimilarListingsModel,
            SortApplied: data.SortApplied,
            DealerHeaderInfo: data.DealerHeader || props.InventoryHeader,
            LocationString: data.LocationString,
            MarketAnalysis: data.MarketAnalysis,
            ListingLocations: prev.ListingLocations,
            ListingMapData: prev.ListingMapData,
            DefaultCenter: prev.DefaultCenter,
            DefaultZoom: prev.DefaultZoom,
            DynamicBlurb: data.DynamicBlurb
        }));

        setFTIWTBs(data.FTIWTBs);
        setDynamicBlurbMobileSplitIndex(data.DynamicBlurbMobileSplitIndex);

        if (data.MarketAnalysis?.Retail?.AveragePrice != forceReloadMA) {
            setMarketAnalysisURL(data.MarketAnalysis?.ChartAjaxRoute);
            setForceReloadMA(data.MarketAnalysis?.Retail?.AveragePrice);
        }

        if (data?.SimilarListingsModel?.SimilarListings?.length > 0) {
            setMobileSearch(false);
        }

        getShippingEstimates(data.ListingIdsForShippingEstimates);
        getAuctionProgress(data.Listings);
        setListingsSearchInProgress(false);
        setShowLoadingRing(false);
        getUserWatchlist(data.Listings);
    };

    const onEmailModalToggle = useCallback(() => {
        if (returnFocusRef) {
            setTimeout(() => {
                returnFocusRef.focus();
            }, 150);
        }
        setShowEmailModal(showEmailModal => !showEmailModal);
    }, [returnFocusRef]);

    const onPrint = () => {
        gtmPushEvent(gtmEventType.printListDetail);
        setLoadImagesForPrint(true);
        dispatchListings({
            type: 'update-listings',
            payload: [...listings].map(listing => {
                return {
                    ...listing,
                    ForceLoadImages: true
                };
            })
        });
    };

    const getPrintUrl = () => {
        var url = criteriaData.SearchPageURL;
        if (props.IsHostedSite && url.includes(constants.IsSegmentSearch) && !url.includes(constants.InventoryType) && url.includes(constants.ScopeCategoryIDs)) {
            url = removeURLParameter(url, constants.IsSegmentSearch);
        }
        return insertURLParameter(url, constants.Print, 1);
    };

    const fireSendAMessageError = useCallback((inquiry, contact, data) => {

        const errorData = {
            CRMID: data.DealerCRMID,
            DealerGroupID: data.DealerGroupID,
            ListingBaseCatID: data.ListingBaseCatID,
            ListingCategoryID: data.ListingCategoryID,
            ListingID: data.ListingID,
            ListingManufacturer: data.ListingManufacturer,
            ListingModel: data.ListingModel,
            ListingSourceDomainUrl: data.ListingSourceDomainUrl,
            ListingURL: data.ListingURL,
            ModalTitle: data.ModalTitle,
            Subject: data.Subject
        }

        let msg = `Error on list page modal open: \r\nInquiry: ${inquiry} \r\nContact: ${contact} \r\nData: ${errorData ?? JSON.stringify(errorData)}`;
        logSAMError(routes.LogSendAMessageError, msg);
    }, []);

    const getFormAssembly = useCallback((modalData) => {
        var m = modalData;
        sandhillsRequest({
            url: routes.GetFormAssembly,
            method: 'post',
            data: {
                FirstName: m.FirstName,
                Lastname: m.Lastname,
                Email: m.Email,
                Phone: m.Phone,
                Country: m.Country,
                PostalCode: m.PostalCode,
                DealerContact: m.DealerContact,
                DealerLocation: m.DealerLocation,
                ListingURL: m.ListingURL,
                ListingID: m.ListingID,
                SerialNumber: m.SerialNumber,
                SiteID: currentSite.SiteID,
                ListingURL: m.ListingURL,
                LanguageID: currentUser?.LanguageID
            },
            errorHandling: {
                message: 'Error retrieving Send-A-Message form.',
                showDetails: false,
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response) {
                setCriteriaData(previousState => {
                    const modalData = {
                        ...previousState,
                        SAMModel: {
                            ...previousState.SAMModel,
                            FormAssembly: response,
                        }
                    }

                    return modalData
                })
            }
        });
    }, [currentUser?.LanguageID]);

    const openEmailModal = useCallback((inquiry, contact, modalData) => {
        if (inquiry != inquiryType.EmailThis && ((!modalData?.DealerGroupID || modalData?.DealerGroupID == 0) && !modalData.IsGeneralOverride) && (!modalData?.ListingID || modalData?.ListingID == 0)) {
            fireSendAMessageError(inquiry, contact, modalData);
        }

        modalData = modalData || {};

        modalData.IsShowroom = props.IsShowroom;

        if (contact != contactType.Wholesale && contact != contactType.ExclusiveWholesale) {
            contact = contactType.Retail;
        }

        if (modalData.HasFormAssembly && modalData.ListingID > 0) {
            getFormAssembly(modalData);
        }

        if (modalData.IsPreAT) {
            modalData.IsPreAT = true;
        } else {
            modalData.IsPreAT = false;
        }
        if (inquiry == inquiryType.EmailThis) {
            modalData.ModalTitle = translator.translate('sandhillssystem.share').toString();

            if (windowSize.isMobile && navigator.share) {
                navigator.share({
                    title: document.title,
                    url: window.location.href
                })
                    .then(() => gtmPushEvent(gtmEventType.MobileShare))
                    .catch(() => setShowShareButtons(true));
                return;
            } else {
                setShowShareButtons(true);
            }
        } else {
            setShowShareButtons(false);
        }

        setTimeout(() => {
            setReturnFocusRef(modalData.Ref || ref.current);
        }, 150);
        setSelectedInquiry(inquiry);
        setSelectedContactType(contact);
        setShowEmailModal(showEmailModal => !showEmailModal);
        setCriteriaData(previousState => {
            return {
                ...previousState, SAMModel: {
                    ...previousState.SAMModel,
                    ...modalData
                }
            };
        });
    }, [
        props.IsShowroom,
        windowSize.isMobile,
        fireSendAMessageError,
        getFormAssembly,
    ]);

    const openBiddingAgreementModal = (data) => {
        setBiddingAgreementProps(data);
        setShowBiddingAgreementModal(true);
    };

    const closeBiddingAggreementModal = () => {
        setBiddingAgreementProps(null);
        setShowBiddingAgreementModal(false);
    };

    const openFinancialCalculator = useCallback((price, lenderImageUrl, lenderUrl, lenderCompanyName, showMonthlyFinanceRatesOnListings, paymentsAsLowAsTerm, paymentsAsLowAsInterestRate, showExtendedLoanTerms) => {
        if (financialCalculatorData.currencies && financialCalculatorData.currencies.length > 0) {
            setFinancialCalculatorData(previousState => {
                return {
                    ...previousState,
                    showFinancialCalculator: true,
                    currentPrice: price,
                    lenderImageUrl: lenderImageUrl,
                    lenderUrl: lenderUrl,
                    lenderCompanyName: lenderCompanyName,
                    showMonthlyFinanceRatesOnListings: showMonthlyFinanceRatesOnListings,
                    paymentsAsLowAsTerm: paymentsAsLowAsTerm,
                    paymentsAsLowAsInterestRate: paymentsAsLowAsInterestRate,
                    showExtendedLoanTerms: showExtendedLoanTerms
                };
            });
        } else {
            getUserSettingsSelectorCurrenciesListAsync(props.CurrencySelector?.CurrenciesSessionStorageKey, props.CurrencySelector?.Currencies, routes.GetCurrenciesAndCultures).then((currenciesList) => {
                setFinancialCalculatorData({
                    showFinancialCalculator: true,
                    currentPrice: price,
                    lenderImageUrl: lenderImageUrl,
                    lenderUrl: lenderUrl,
                    lenderCompanyName: lenderCompanyName,
                    currencies: currenciesList,
                    showMonthlyFinanceRatesOnListings: showMonthlyFinanceRatesOnListings,
                    paymentsAsLowAsTerm: paymentsAsLowAsTerm,
                    paymentsAsLowAsInterestRate: paymentsAsLowAsInterestRate,
                    showExtendedLoanTerms: showExtendedLoanTerms
                });
            });
        }
    }, [
        financialCalculatorData.currencies,
        getUserSettingsSelectorCurrenciesListAsync,
        props.CurrencySelector?.CurrenciesSessionStorageKey,
        props.CurrencySelector?.Currencies,
    ]);

    const closeFinancialCalculator = () => {
        setFinancialCalculatorData(previousState => {
            return {
                ...previousState,
                showFinancialCalculator: false
            };
        });
    };

    const updateCompareListing = useCallback((listingID, mediaId, isChecked, imageUrl) => {
        mediaId = mediaId || 0;
        imageUrl = imageUrl || '';

        if (isChecked && tryAddListingToCompareCookie(listingID, mediaId)) {
            setSelectedCompareListings(previousState => {
                return [
                    ...previousState,
                    { ListingID: listingID, ImageUrl: imageUrl }
                ];
            });
        } else if (!isChecked && tryDeleteCompareListingFromCookie(listingID)) {
            setSelectedCompareListings(previousState => {
                return [...previousState].filter(listing => listing.ListingID != listingID);
            });
        }
    }, []);

    const renderListing = (listing, index, isSimilar) => {
        switch (CardType.toUpperCase()) {
            case 'GRID':
                return (
                    <MemoWebListing_GridCard
                        {...listing}
                        LazyLoad={index > (windowSize.width >= 700 && windowSize.width < 1000 ? 2 : windowSize.isMobile ? 1 : 4)}
                        ForceLoadImages={listing.ForceLoadImages || index < (windowSize.width >= 700 && windowSize.width < 1000 ? 2 : windowSize.isMobile ? 1 : 4)}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        IsWatchList={props.IsWatchList}
                        IsWatchlistMyBids={props.IsWatchlistMyBids}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        ListPageSpecMaxLength={props.ListPageSpecMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        ShippingEstimateData={searchData.ShippingEstimates[listing.Id]}
                        openFinancialCalculator={openFinancialCalculator}
                        WatchlistURL={props.WatchlistURL}
                        SetBuyersPremiumData={setBuyersPremiumData}
                        WatchlistTitle={props.WatchlistTitle}
                        UserWatchlist={userWatchlist}
                        WatchlistClickDelete={watchlistClickDelete}
                        openEmailModal={openEmailModal}
                        HasFormAssembly={props.FacetedSearchAjaxModel.SAMModel.HasFormAssembly}
                        IsCompareChkBoxDisabled={selectedCompareListings && selectedCompareListings.length >= 4 && selectedCompareListings.filter(c => c.ListingID == listing.Id)?.length == 0}
                        UpdateCompareListing={updateCompareListing}
                        IsCheckedForCompare={selectedCompareListings && selectedCompareListings.filter(c => c.ListingID == listing.Id).length > 0}
                        ShowCompareURL={selectedCompareListings?.length > 0}
                        ShippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                        CompareUrl={props.CompareUrl}
                        UseDataProperties={props.UseDataProperties}
                        LocationString={searchData.LocationString}
                        PricingInfo={getListingPricingInfo(listing)}
                        DispatchListings={dispatchListings}
                        resetAuctionData={resetAuctionDataForListing}
                        sortApplied={searchData.SortApplied}
                        LoginLink={criteriaData.LoginLink}
                        CustomWatchlists={props.ListPageAjaxModel.CustomWatchLists}
                        CustomWatchListLinks={props.ListPageAjaxModel.CustomWatchListLinks}
                        getUserWatchlist={getUserWatchlist}
                        addToWatchlistCount={addToWatchlistCount}
                        IsEventPage={searchData.DealerHeaderInfo?.EventID > 0}
                        notificationSettings={props.notificationSettings}
                        RequestAQuote={props.RequestAQuote}
                        IsRY={isRY}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        IsRecommendedListingsSearch={props.IsRecommendedListingsSearch}
                        ShowPreAuctionTime={listing.PreAuctionTimeDate ? true : false}
                        IsSimilar={isSimilar}
                        IsFTISearch={isFTISearch}
                        setCountdownListings={setCountdownListings}
                        endTime={(listing.AuctionBiddingData?.TimeRemaining <= secondsLeftToAddListingToCountdown || listingShouldUseAuctionUpdatePolling(listing)) ? listing.AuctionBiddingData?.ProjectedEndDateTime : undefined}   //Checking if time remaining <= max seconds listing can be extended because this is used as threshold to start auction update requests.  When auction update requests start, a re-render occurs and we don't want this to reset the time remaining back to what it was on page load. Convert time remaining to milliseconds for endTime, since the react-countdown expects countdown to target to be in milliseconds
                        showAllSpecs={showAllSpecs}
                        expandDescription={expandAllDescriptions}
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                        forceAddToCountdownListings={listingShouldUseAuctionUpdatePolling(listing)}
                        quickBiddingEnabled={quickBiddingEnabled}
                        quickBiddingError={quickBiddingError}
                        setQuickBidError={setQuickBidError}
                        setWaitingForQuickBidAttemptResponseFlag={setWaitingForQuickBidAttemptResponseFlag}
                        CanBidOnATListingsFromListPage={props.CanBidOnATListingsFromListPage}
                    />
                );
            case "MOBILE":
                return (
                    <WebListing_MobileCard
                        {...listing}
                        LazyLoad={index >= (windowSize.width >= 700 && windowSize.width < 1000 ? 2 : windowSize.isMobile ? 1 : 0)}
                        ForceLoadImages={listing.ForceLoadImages || index < (windowSize.width >= 700 && windowSize.width < 1000 ? 2 : windowSize.isMobile ? 1 : 0)}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        IsWatchList={props.IsWatchList}
                        IsWatchlistMyBids={props.IsWatchlistMyBids}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        ListPageSpecMaxLength={props.ListPageSpecMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        ShippingEstimateData={searchData.ShippingEstimates[listing.Id]}
                        openFinancialCalculator={openFinancialCalculator}
                        WatchlistURL={props.WatchlistURL}
                        SetBuyersPremiumData={setBuyersPremiumData}
                        WatchlistTitle={props.WatchlistTitle}
                        UserWatchlist={userWatchlist}
                        WatchlistClickDelete={watchlistClickDelete}
                        openEmailModal={openEmailModal}
                        HasFormAssembly={props.FacetedSearchAjaxModel.SAMModel.HasFormAssembly}
                        IsCompareChkBoxDisabled={selectedCompareListings && selectedCompareListings.length >= 4 && selectedCompareListings.filter(c => c.ListingID == listing.Id)?.length == 0}
                        UpdateCompareListing={updateCompareListing}
                        IsCheckedForCompare={selectedCompareListings && selectedCompareListings.filter(c => c.ListingID == listing.Id).length > 0}
                        ShowCompareURL={selectedCompareListings?.length > 0}
                        ShippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                        CompareUrl={props.CompareUrl}
                        UseDataProperties={props.UseDataProperties}
                        LocationString={searchData.LocationString}
                        PricingInfo={getListingPricingInfo(listing)}
                        DispatchListings={dispatchListings}
                        resetAuctionData={resetAuctionDataForListing}
                        sortApplied={searchData.SortApplied}
                        LoginLink={criteriaData.LoginLink}
                        CustomWatchlists={props.ListPageAjaxModel.CustomWatchLists}
                        CustomWatchListLinks={props.ListPageAjaxModel.CustomWatchListLinks}
                        getUserWatchlist={getUserWatchlist}
                        addToWatchlistCount={addToWatchlistCount}
                        IsEventPage={searchData.DealerHeaderInfo?.EventID > 0}
                        notificationSettings={props.notificationSettings}
                        RequestAQuote={props.RequestAQuote}
                        IsRY={isRY}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        IsRecommendedListingsSearch={props.IsRecommendedListingsSearch}
                        ShowPreAuctionTime={listing.PreAuctionTimeDate ? true : false}
                        IsSimilar={isSimilar}
                        IsFTISearch={isFTISearch}                        
                        setCountdownListings={setCountdownListings}
                        endTime={(listing.AuctionBiddingData?.TimeRemaining <= secondsLeftToAddListingToCountdown || forceAuctionUpdatePollingForListingsInAuctionUpdateCache) ? listing.AuctionBiddingData?.ProjectedEndDateTime : undefined}   //Checking if time remaining <= max seconds listing can be extended because this is used as threshold to start auction update requests.  When auction update requests start, a re-render occurs and we don't want this to reset the time remaining back to what it was on page load. Convert time remaining to milliseconds for endTime, since the react-countdown expects countdown to target to be in milliseconds
                        showAllSpecs={showAllSpecs}
                        expandDescription={expandAllDescriptions}
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                        forceAddToCountdownListings={listingShouldUseAuctionUpdatePolling(listing)}
                        quickBiddingEnabled={quickBiddingEnabled}
                        quickBiddingError={quickBiddingError}
                        setQuickBidError={setQuickBidError}
                        setWaitingForQuickBidAttemptResponseFlag={setWaitingForQuickBidAttemptResponseFlag}
                        CanBidOnATListingsFromListPage={props.CanBidOnATListingsFromListPage}   
                    />
                );
            case 'LARGECARD':
                return (
                    <MemoWebListing_LargeCard
                        {...listing}
                        LazyLoad={index != 0}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        IsWatchList={props.IsWatchList}
                        IsWatchlistMyBids={props.IsWatchlistMyBids}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        HasFormAssembly={props.FacetedSearchAjaxModel.SAMModel.HasFormAssembly}
                        ShippingEstimateData={searchData.ShippingEstimates[listing.Id]}
                        openFinancialCalculator={openFinancialCalculator}
                        ShowDealer={props.ShowDealerOnCardLayouts}
                        openEmailModal={openEmailModal}
                        IsCompareChkBoxDisabled={selectedCompareListings && selectedCompareListings.length >= 4}
                        UpdateCompareListing={updateCompareListing}
                        IsCheckedForCompare={selectedCompareListings && selectedCompareListings.filter(c => c.ListingID == listing.Id).length > 0}
                        ShippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                        CompareUrl={props.CompareUrl}
                        PricingInfo={getListingPricingInfo(listing)}
                        LocationString={searchData.LocationString}
                        UseDataProperties={props.UseDataProperties}
                        DispatchListings={dispatchListings}
                        sortApplied={searchData.SortApplied}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                    />
                );
            case 'SMALLCARD':
                return (
                    <MemoWebListing_SmallCard
                        {...listing}
                        LazyLoad={index != 0}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        IsWatchList={props.IsWatchList}
                        IsWatchlistMyBids={props.IsWatchlistMyBids}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        HasFormAssembly={props.FacetedSearchAjaxModel.SAMModel.HasFormAssembly}
                        ShippingEstimateData={searchData.ShippingEstimates[listing.Id]}
                        openFinancialCalculator={openFinancialCalculator}
                        ShowDealer={props.ShowDealerOnCardLayouts}
                        openEmailModal={openEmailModal}
                        IsCompareChkBoxDisabled={selectedCompareListings && selectedCompareListings.length >= 4}
                        UpdateCompareListing={updateCompareListing}
                        IsCheckedForCompare={selectedCompareListings && selectedCompareListings.filter(c => c.ListingID == listing.Id).length > 0}
                        ShippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                        CompareUrl={props.CompareUrl}
                        UseDataProperties={props.UseDataProperties}
                        PricingInfo={getListingPricingInfo(listing)}
                        LocationString={searchData.LocationString}
                        DispatchListings={dispatchListings}
                        sortApplied={searchData.SortApplied}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                    />
                );
            case 'THUMBCARD':
                return (
                    <MemoWebListing_ThumbCard
                        {...listing}
                        LazyLoad={index != 0}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        IsWatchList={props.IsWatchList}
                        IsWatchlistMyBids={props.IsWatchlistMyBids}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        HasFormAssembly={props.FacetedSearchAjaxModel.SAMModel.HasFormAssembly}
                        ShippingEstimateData={searchData.ShippingEstimates[listing.Id]}
                        openFinancialCalculator={openFinancialCalculator}
                        ShowDealer={props.ShowDealerOnCardLayouts}
                        openEmailModal={openEmailModal}
                        UseDataProperties={props.UseDataProperties}
                        IsCompareChkBoxDisabled={selectedCompareListings && selectedCompareListings.length >= 4}
                        UpdateCompareListing={updateCompareListing}
                        IsCheckedForCompare={selectedCompareListings && selectedCompareListings.filter(c => c.ListingID == listing.Id).length > 0}
                        ShippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                        CompareUrl={props.CompareUrl}
                        PricingInfo={getListingPricingInfo(listing)}
                        LocationString={searchData.LocationString}
                        DispatchListings={dispatchListings}
                        sortApplied={searchData.SortApplied}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                    />
                );
            case 'BOXED':
                return (
                    <MemoWebListing_Boxed
                        {...listing}
                        LazyLoad={index != 0}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        IsWatchList={props.IsWatchList}
                        IsWatchlistMyBids={props.IsWatchlistMyBids}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        HasFormAssembly={props.FacetedSearchAjaxModel.SAMModel.HasFormAssembly}
                        ShippingEstimateData={searchData.ShippingEstimates[listing.Id]}
                        openFinancialCalculator={openFinancialCalculator}
                        UseDataProperties={props.UseDataProperties}
                        ShowDealer={props.ShowDealerOnCardLayouts}
                        openEmailModal={openEmailModal}
                        IsCompareChkBoxDisabled={selectedCompareListings && selectedCompareListings.length >= 4}
                        UpdateCompareListing={updateCompareListing}
                        IsCheckedForCompare={selectedCompareListings && selectedCompareListings.filter(c => c.ListingID == listing.Id).length > 0}
                        ShippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                        CompareUrl={props.CompareUrl}
                        LocationString={searchData.LocationString}
                        PricingInfo={getListingPricingInfo(listing)}
                        DispatchListings={dispatchListings}
                        sortApplied={searchData.SortApplied}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                    />
                );
            case 'STOLENEQUIPMENT':
                return (
                    <MemoStolenEquipmentListCard
                        {...listing}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        DispatchListings={dispatchListings}
                        UseDataProperties={props.UseDataProperties}
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                    />
                );
            case 'EVENTS':
                return (
                    <MemoEventListing
                        {...listing}
                        IsWatchList={props.IsWatchList}
                        openBiddingAgreementModal={openBiddingAgreementModal}
                        openAlertPreferenceModal={openAlertPreferenceModal}
                        setListings={dispatchListings}
                        watchlistDeleteSuccess={watchlistDeleteSuccess}
                        listings={listings}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        IsRecommendedAuctionsSearch={props.IsRecommendedAuctionsSearch}
                        ShowAltBackground={index % 2 == 0}
                        MaxDescriptionLength={constants.MaxDescriptionLength}
                    />
                );
            case 'WEBLISTING':
            default:
                return (
                    <MemoWebListing
                        {...listing}
                        LazyLoad={index != 0}
                        ForceLoadImages={listing.ForceLoadImages || index == 0}
                        ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                        IsWatchList={props.IsWatchList}
                        IsWatchlistMyBids={props.IsWatchlistMyBids}
                        ListPageDescriptionMaxLength={props.ListPageDescriptionMaxLength}
                        ListPageSpecMaxLength={props.ListPageSpecMaxLength}
                        IsSearchEngine={currentUser.IsSearchEngine}
                        HasFormAssembly={props.FacetedSearchAjaxModel.SAMModel.HasFormAssembly}
                        ShippingEstimateData={searchData.ShippingEstimates[listing.Id]}
                        openFinancialCalculator={openFinancialCalculator}
                        WatchlistURL={props.WatchlistURL}
                        SetBuyersPremiumData={setBuyersPremiumData}
                        WatchlistTitle={props.WatchlistTitle}
                        UserWatchlist={userWatchlist}
                        WatchlistClickDelete={watchlistClickDelete}
                        openEmailModal={openEmailModal}
                        UseDataProperties={props.UseDataProperties}
                        IsCompareChkBoxDisabled={selectedCompareListings && selectedCompareListings.length >= 4}
                        UpdateCompareListing={updateCompareListing}
                        IsCheckedForCompare={selectedCompareListings && selectedCompareListings.filter(c => c.ListingID == listing.Id).length > 0}
                        ShippingEstimateImageUrl={props.ShippingEstimateImageUrl}
                        CompareUrl={props.CompareUrl}
                        LocationString={searchData.LocationString}
                        PricingInfo={getListingPricingInfo(listing)}
                        DispatchListings={dispatchListings}
                        resetAuctionData={resetAuctionDataForListing}
                        sortApplied={searchData.SortApplied}
                        LoginLink={criteriaData.LoginLink}
                        CustomWatchlists={props.ListPageAjaxModel.CustomWatchLists}
                        CustomWatchListLinks={props.ListPageAjaxModel.CustomWatchListLinks}
                        getUserWatchlist={getUserWatchlist}
                        addToWatchlistCount={addToWatchlistCount}
                        IsEventPage={searchData.DealerHeaderInfo?.EventID > 0}
                        notificationSettings={props.notificationSettings}
                        RequestAQuote={props.RequestAQuote}
                        IsRY={isRY}
                        ShowAuctionBranding={props.ShowAuctionBranding}
                        IsRecommendedListingsSearch={props.IsRecommendedListingsSearch}                        
                        setCountdownListings={setCountdownListings}
                        endTime={(listing.AuctionBiddingData?.TimeRemaining <= secondsLeftToAddListingToCountdown || listingShouldUseAuctionUpdatePolling(listing)) ? listing.AuctionBiddingData?.ProjectedEndDateTime : undefined} //Checking if time remaining <= max seconds listing can be extended because this is used as threshold to start auction update requests.  When auction update requests start, a re-render occurs and we don't want this to reset the time remaining back to what it was on page load. Convert time remaining to milliseconds for endTime, since the react-countdown expects countdown to target to be in milliseconds
                        AriaLabelTitle={listing.ListingTitle + " (" + (index + 1) + ")"}
                        forceAddToCountdownListings={listingShouldUseAuctionUpdatePolling(listing)}                        
                    />
                );
        }
    };

    const renderBannerAd = (index) => {
        if (searchData.DoubleClickAdCollectionViewModel.ShouldRenderDoubleClickAds) {
            const middleIndex = (useGridView && props.UseNewListPageDesign && !windowSize.isMobile) ? searchData.DoubleClickAdCollectionViewModel.MiddleIndex : constants.MiddleIndex;
            if (props.UseAlternateAdPlacement) {
                if (index === middleIndex && searchData.DoubleClickAdCollectionViewModel.MiddleAdvertisement) {
                    return (
                        <DoubleClickAd
                            {...memoDoubleClickAdCollection}
                            key={searchData.DoubleClickAdCollectionViewModel.MiddleAdvertisement.AdUnitCode}
                            ad={searchData.DoubleClickAdCollectionViewModel.MiddleAdvertisement}
                            style={useGridView ? { padding: 0 } : undefined}
                            position={bannerAdPositions.Middle}
                            contained
                        />
                    );
                }

                const _index = (useGridView && !windowSize.isMobile) ? (index - 1) : index;
                if (_index === searchData.DoubleClickAdCollectionViewModel.BottomIndex && searchData.DoubleClickAdCollectionViewModel.BottomAdvertisement) {
                    return (
                        <DoubleClickAd
                            {...memoDoubleClickAdCollection}
                            key={searchData.DoubleClickAdCollectionViewModel.BottomAdvertisement.AdUnitCode}
                            ad={searchData.DoubleClickAdCollectionViewModel.BottomAdvertisement}
                            style={useGridView ? { padding: 0 } : undefined}
                            position={bannerAdPositions.Bottom}
                            contained
                        />
                    );
                }
            } else if (index === middleIndex && searchData.DoubleClickAdCollectionViewModel.BottomAdvertisement) {
                return (
                    <DoubleClickAd
                        {...memoDoubleClickAdCollection}
                        key={searchData.DoubleClickAdCollectionViewModel.BottomAdvertisement.AdUnitCode}
                        ad={searchData.DoubleClickAdCollectionViewModel.BottomAdvertisement}
                        style={useGridView ? { padding: 0 } : undefined}
                        position={bannerAdPositions.Bottom}
                        contained
                    />
                );
            }
        }

        return (<></>);
    };

    useEffect(() => {
        if ((CardType === 'GRID' || CardType === 'MOBILE') && listings?.length > 0 && !listingsSearchInProgress) {
            // Give time for all cards to mount and render
            const timeoutId = setTimeout(() => {
                adjustElementHeightsByRow('list-listing-title');
                adjustElementHeightsByRow('below-price-container');
                adjustElementHeightsByRow('price-contain');
                adjustElementHeightsByRow('widget-container');
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [listings, CardType, listingsSearchInProgress, adjustElementHeightsByRow]);

    // Also trigger on window resize
    useEffect(() => {
        if ((CardType === 'GRID' || CardType === 'MOBILE') && listings?.length > 0) {
            const timeoutId = setTimeout(() => {
                if (CardType === 'GRID') {
                    adjustElementHeightsByRow('list-listing-title');
                }
                adjustElementHeightsByRow('below-price-container');
                adjustElementHeightsByRow('price-contain');
                adjustElementHeightsByRow('widget-container');
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [windowSize.width, adjustElementHeightsByRow, listings]);

    const createPricingInfoHash = (listing) => [
        listing?.PreAuctionTimeDate ?? '',
        listing?.ShowWholesalePriceOnly ?? '',
        listing?.ShowWholeSale ?? '',
        listing?.WholeSalePrice ?? '',
        listing?.IsExclusiveWholeSale ?? '',
        listing?.PriceGroup ?? ''
    ].join('|');

    const getListingPricingInfo = useCallback((listing) => {
        if (!listing) return null;
        const oldPricingInfo = pricingInfoRef.current.get(listing.Id);
        // Recompute only if relevant fields changed
        const listingHash = createPricingInfoHash(listing);
        if (oldPricingInfo && oldPricingInfo.hash === listingHash) {
            return oldPricingInfo.info;
        }
        const newPricingInfo = {
            ShowPreAuctionTime: (listing.PreAuctionTimeDate ? true : false),
            PreAuctionTimeDate: listing.PreAuctionTimeDate,
            ShowWholesalePriceOnly: listing.ShowWholesalePriceOnly,
            ShowWholeSale: listing.ShowWholeSale,
            WholeSalePrice: listing.WholeSalePrice,
            IsExclusiveWholeSale: listing.IsExclusiveWholeSale,
            PriceGroup: listing.PriceGroup
        };
        pricingInfoRef.current.set(listing.Id, { info: newPricingInfo, hash: listingHash });
        return newPricingInfo;
    }, []);

    const highlightFacets = useCallback(() => {
        if (window.innerWidth <= 785) {
            toggleMobileFilter(true);
        } else {
            setHighlightSelectedFacets(true);
            document.getElementById('filters')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [toggleMobileFilter]);

    useEffect(() => {
        if (searchData.ListingsCount === 0 && !props.Message) {
            gtmPushEvent(gtmEventType.noListingsFound);
        }
    }, []);
    const renderError = useCallback(() => {
        return (
            <div className='list-error-container'>
                {props.Message ?
                    <>
                        <div className='info'><strong dangerouslySetInnerHTML={{ __html: props.Message }}></strong></div>
                        {props.CanRequestAccessExclusiveWholesaleSite &&
                            <div className='info bold'><br />
                                <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); submitRequestForExclusiveWholesaleAccess(e); } }} onClick={(e) => submitRequestForExclusiveWholesaleAccess(e)} className='capitalize'><strong><TranslationText tKey='sandhills.tradepubwebsite.mvc.clickhere' /></strong></a>
                                <TranslationText tKey='sandhills.torequestaccess' replaceValues={[' ']} />
                                {requestExclusiveWholesaleAccess.message &&
                                    <div className='info'><br />
                                        <strong>
                                            {requestExclusiveWholesaleAccess.message}
                                        </strong>
                                    </div>}
                            </div>}
                    </>
                    :
                    <div className='info'>
                        <p
                            className="no-exact-matches"
                            onClick={(e) => { if (e.target.closest('[data-action="highlight-facets"]')) highlightFacets(); }}
                            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('[data-action="highlight-facets"]')) { e.preventDefault(); highlightFacets(); } }}
                            dangerouslySetInnerHTML={{ __html: translator.translate("sandhills.tradesites.mvc.nomatchestryadjustingfilters") }}
                        />
                        {similarListings?.length > 0 &&
                            <div className="closest-options-text">{translator.translate("sandhills.tradesites.mvc.herestheclosestoptionstohelpyougetstarted")}</div>
                        }
                    </div>}
            </div>
        );
    }, [
        props.Message,
        props.CanRequestAccessExclusiveWholesaleSite,
        props.IsEvents,
        criteriaData.SelectedFacets,
        criteriaData.SearchBaseURL,
        clearAll,
        clearFacetDebounced,
        requestExclusiveWholesaleAccess.message,
        similarListings,
        highlightFacets
    ]);

    const submitRequestForExclusiveWholesaleAccess = (e) => {
        e.preventDefault();
        if (!requestExclusiveWholesaleAccess.hasRequestedAccess) {
            sandhillsRequest({
                url: routes.SubmitExclusiveWholesaleAccessRequest,
                method: 'post',
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                setRequestExclusiveWholesaleAccess({
                    hasRequestedAccess: true,
                    message: data
                });
            });
        }
    };

    const displayPagination = (data) => {
        return (data.TotalPages > 1 || (props.IsWatchList && data.TotalPages == 1 && data.CurrentPage > 1));
    };

    const scrollToBlurb = () => {
        if (listBlurbRef.current) {
            listBlurbRef.current.scrollIntoView();
        }
    };

    const onAlertModalClose = () => {
        setAlertModalStatus({ show: false, message: '', shouldRefreshPageOnClose: false });
    };

    const buildDistanceSortProps = () => {
        return {
            OnUpdate: onDistanceLocationModalUpdate,
            LocationAutosuggestUrl: routes.LocationAutosuggestUrl,
            AjaxGetCoordinatesFromLocationStringUrl: routes.AjaxGetCoordinatesFromLocationStringUrl,
            AjaxFormattedLocationStringFromCurrentCoordinatesUrl: routes.FormattedLocationStringFromCurrentCoordinates,
            CurrentAuthenticatedUserLocationForCoordinates: currentUser.CurrentLocation?.CurrentAuthenticatedUserLocationForCoordinates,
            onSortChange: delayDistance,
            DistanceSortIsSelected: criteriaData.SelectedSortValue == searchData.distanceSortID,
            DisplayShowClosestFirstByConsent: searchData.DisplayShowClosestFirstByConsent,
            distanceSortID: searchData.distanceSortID,
            defaultSortID: searchData.defaultSortID,
            CurrentUserFormattedAddress: searchData.LocationString,
            ToggleDistanceModal: onDistanceLocationModalToggle,
            ShowDistanceSortError: showDistanceSortError,
            setShowDistanceModal: setShowDistanceModal
        };
    };

    const distanceSortProps = useMemo(() => buildDistanceSortProps(), [
        criteriaData.SelectedSortValue,
        searchData.distanceSortID,
        searchData.defaultSortID,
        searchData.LocationString,
        showDistanceSortError,
        currentUser.CurrentLocation
    ]);

    const showFilter = () => {
        return props.ShowFilter && (criteriaData.SelectedFacets?.length > 0 || criteriaData.SearchFields?.length > 0);
    };

    const openSavedSearchModal = useCallback((e, isSave) => {
        setSavedSearchData(previousState => {
            return {
                ...previousState,
                Loading: true,
                PendingSave: isSave
            };
        });
        if (currentUser.IsAuthenticated && !savedSearchData.SearchCompleted && (!savedSearchData.WebSavedSearches || savedSearchData.WebSavedSearches.length <= 0)) {
            getWebSavedSearches();
        }
        else {
            setSavedSearchData(previousState => {
                return { ...previousState, ShowModal: true };
            });
            if (savedSearchData.WebSavedSearches?.length >= webSavedSearchAuthenticatedLimit) {
                setSavedSearchData(previousState => {
                    return {
                        ...previousState, Message: translator.translate("sandhills.savedsearchlimitreached")
                    };
                });
            } else if (savedSearchData.WebSavedSearches?.length <= 0 && !isSave) {
                setSavedSearchData(previousState => {
                    return {
                        ...previousState, Message: translator.translate("sandhills.nosearchessaved")
                    };
                });
            }
            setSavedSearchData(previousState => {
                return {
                    ...previousState, Loading: false,
                    PendingSave: isSave
                };
            });
        }


    }, [currentUser.IsAuthenticated]);

    const closeSavedSearchModal = useCallback(() => {
        setSavedSearchData(previousState => {
            return {
                ...previousState,
                PendingSave: false,
                ShowModal: false,
                Message: ' '
            };
        });
    }, []);

    const openAlertPreferenceModal = (eventID, eventCategoryID) => {
        if (!currentUser.IsAuthenticated) {
            window.location.href = routes.SSO;
        }

        if (eventID && eventID > 0) {
            setEventIDForAlertPreferenceModal(eventID);
        }

        let siteName = "";

        if (eventCategoryID && eventCategoryID > 0) {
            setEventCategoryIDForAlertPreferenceModal(eventCategoryID);
            siteName = eventCategoryID === eventCategory.onlineAuction ? "AuctionTime" : "EquipmentFacts";
        }

        if (!isLoading) {
            setIsLoading(true);
            sandhillsRequest({
                url: routes.GetUserAuctionAlertPreferences,
                method: 'post',
                data: {
                    siteName: siteName
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                setIsLoading(false);
                if (data) {
                    setNotificationSettings(data);
                    setShowAlertPreferenceModal(!showAlertPreferenceModal);
                }
            }).catch(() => {
                setIsLoading(false);
            });
        }
    };

    const getWebSavedSearches = useCallback(() => {
        if (currentUser.IsAuthenticated) {
            sandhillsRequest({
                url: routes.GetWebSavedSearchesForCurrentUser,
                errorHandling: {
                    message: translator.translate('sandhills.savesearcherroroccurred'),
                    useConsole: false,
                    useAlert: true
                }
            }).then((response) => {
                let msg = '';
                let webSavedSearches = [];
                if (response?.length > 0) {
                    if (response.length >= webSavedSearchAuthenticatedLimit) {
                        msg = translator.translate("sandhills.savedsearchlimitreached");
                    }
                    webSavedSearches = response;
                }
                else {
                    msg = translator.translate("sandhills.nosearchessaved");
                }
                setSavedSearchData(previousState => {
                    return {
                        ...previousState,
                        Message: msg,
                        WebSavedSearches: webSavedSearches,
                        Loading: false,
                        ShowModal: true,
                        SearchCompleted: true,
                        PendingSave: previousState.PendingSave && webSavedSearches
                    };
                });
            }).catch(() => {
                setSavedSearchData(previousState => {
                    return {
                        ...previousState,
                        Loading: false
                    };
                });
            });
        }
    }, [currentUser.IsAuthenticated]);

    const removeWebSavedSearch = useCallback((id) => {
        setSavedSearchData(previousState => {
            return {
                ...previousState,
                Loading: true
            };
        });
        sandhillsRequest({
            url: routes.DeleteWebSavedSearch,
            method: 'post',
            data: { webSavedSearchID: id },
            errorHandling: {
                message: translator.translate('sandhills.savesearcherroroccurreddelete'),
                useConsole: false,
                useAlert: true
            }
        }).then((response) => {
            if (response.Success) {
                getWebSavedSearches();
            }
        }).catch(() => {
            setSavedSearchData(previousState => {
                return {
                    ...previousState,
                    Loading: false
                };
            });
        });
    }, [getWebSavedSearches]);

    const setPendingSave = useCallback((value) => {
        setSavedSearchData(previousState => {
            return {
                ...previousState,
                PendingSave: value
            };
        });
    }, []);

    const updateWebSavedSearch = useCallback((id, newName) => {

        let webSavedSearchModel = {
            SearchName: newName,
            WebSavedSearchID: id
        };
        sandhillsRequest({
            url: routes.SaveWebSavedSearch,
            method: 'post',
            data: webSavedSearchModel,
            errorHandling: {
                message: translator.translate('sandhills.savesearcherroroccurred'),
                useConsole: false,
                useAlert: true
            }
        }).then((response) => {
            if (response.Success) {
                setSavedSearchData(previousState => {
                    return {
                        ...previousState,
                        PendingSave: false
                    };
                });
                getWebSavedSearches();
            }
        }).catch(() => {
            setSavedSearchData(previousState => {
                return {
                    ...previousState,
                    Loading: false
                };
            });
        });
    }, [getWebSavedSearches]);

    const saveCurrentSearch = useCallback((name) => {
        if (name) {
            if (savedSearchData.WebSavedSearches?.length < webSavedSearchAuthenticatedLimit) {
                if (savedSearchData.WebSavedSearches?.length > 0 && savedSearchData.WebSavedSearches.map(savedSearch => savedSearch.SearchName).includes(name)) {
                    setSavedSearchData(previousState => {
                        return {
                            ...previousState,
                            Message: translator.translate("sandhills.SavedSearchNameExists")
                        };
                    });
                }
                else {
                    if (!currentUser.IsAuthenticated) {
                        window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href + (window.location.href.includes('?') ? '&' : '?') + 'wssname=' + name + '&wssnum=' + searchData.ListingsCount);
                    } else {
                        setSavedSearchData(previousState => {
                            return {
                                ...previousState,
                                Loading: true
                            };
                        });
                        let webSavedSearchModel = {
                            SearchName: name,
                            NumberMatchesFound: searchData.ListingsCount,
                            SearchURL: criteriaData.SearchBaseURL
                        };
                        sandhillsRequest({
                            url: routes.SaveWebSavedSearch,
                            method: 'post',
                            data: webSavedSearchModel,
                            errorHandling: {
                                message: translator.translate('sandhills.savesearcherroroccurred'),
                                useConsole: false,
                                useAlert: true
                            }
                        }).then((response) => {
                            if (response.Success) {
                                setSavedSearchData(previousState => {
                                    return {
                                        ...previousState,
                                        PendingSave: false
                                    };
                                });
                                getWebSavedSearches();
                            }
                        }).catch(() => {
                            setSavedSearchData(previousState => {
                                return {
                                    ...previousState,
                                    Loading: false
                                };
                            });
                        });
                    }
                }
            }
            else {
                setSavedSearchData(previousState => {
                    return {
                        ...previousState,
                        Message: translator.translate("sandhills.savedsearchlimitreached")
                    };
                });
            }
        } else {
            setSavedSearchData(previousState => {
                return {
                    ...previousState,
                    Message: translator.translate("sandhills.savedsearchentername")
                };
            });
        }
    }, [
        currentUser.IsAuthenticated,
        getWebSavedSearches,
        searchData.ListingsCount,
        criteriaData.SearchBaseURL
    ]);

    const renderSCFBarPlaceholder = () => {
        return (
            <div className='show-closest-first-container'>
                <div className='show-closest-first'>
                    {(searchData.SortOrderItems && searchData.SortOrderItems.length > 0) && !windowSize.isMobile &&
                        <div style={{ marginLeft: 'auto' }}>
                            <SortDropdown
                                IsDesktop={!mobileSearch}
                                SortOptions={searchData.SortOrderItems}
                                OnChange={onSortChange}
                                SelectedValue={criteriaData.SelectedSortValue > 0 ? criteriaData.SelectedSortValue : searchData.defaultSortID}
                                SortButton={true}
                                ShowMobileSort={showMobileSort}
                                toggleMobileSort={toggleMobileSort}
                                UseLimitedBar={!props.UseNewListPageDesign}
                            />
                        </div>
                    }
                </div>
            </div>
        );
    };

    const openQuickBidInfoModal = () => {
        setShowQuickBidInfoModal(true);
    };

    const onQuickBidInfoModalClose = () => {
        setShowQuickBidInfoModal(false);
    };

    const toggleQuickBidSetting = (promptConfirmation) => {
        if (promptConfirmation && !quickBiddingEnabled) {
            setShowQuickBidActivationModal(true); // show modal to confirm enabling QuickBid
        }
        else {
            setIsLoading(true);
            sandhillsRequest({
                url: routes.SaveUserQuickBidSettings,
                data: { quickBidEnabled: !quickBiddingEnabled },
                errorHandling: {
                    message: 'An error occurred while saving your Quick Bid setting. Please refresh your page and try again.',
                    useConsole: false,
                    useAlert: true
                }
            }).then((data) => {
                setQuickBiddingEnabled(!quickBiddingEnabled);
            })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }

    const shouldShowQuickBidToggle = () => {
        return (currentSite?.Site?.IsAuctionTime && props?.CanBidOnATListingsFromListPage == true && currentUser?.IsAuthenticated && currentUser?.BidderStatus == bidderStatus.ACTIVE &&
            !criteriaData?.IsAuctionResults && !props?.IsEvents && !props?.IsAuctionResult);
    }

    const renderQuickBidToggle = () => {
        return (
            <div className="qb-toggle-row">
                <div className="qb-toggle">
                    <a className="info-btn" onClick={() => openQuickBidInfoModal()} >
                        <img src={routes.CDNBaseUrl + 'Images/Siteart/AuctionTime/quickbidyellow.png'} />
                        <i className="fa-regular fa-circle-question"></i>
                    </a>
                    <div class="toggle">
                        <input type="checkbox" name="quickbid" checked={quickBiddingEnabled} onChange={(e) => toggleQuickBidSetting(e.target.checked)} />
                            <div class="switch"></div>
                            <div class="track"></div>
                    </div>
                </div>
            </div>
        );
    };

    const renderBidAgreementBlurb = () => {
        return (
            <div className="quick-bid-disclaimer" dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.biddingwebagreementdisclaimer', [routes.TermsOfUse]) }} />
        );
    };  

    const setQuickBidError = (quickBidError) => {
        setQuickBiddingError(quickBidError);        
    }

    const renderExclusiveAuctionsBar = () => (
        <div className="exclusive-auctions">
            <i className="fa fa-lock" aria-hidden="true"></i> {translator.translate('sandhills.exclusiveauctions')}
        </div>
    );

    const toggleBetaSiteCookie = () => {
        const newUseBetaSiteCookie = !useBetaSiteCookie;
        setUseBetaSiteCookie(newUseBetaSiteCookie);
        if (newUseBetaSiteCookie) {
            setBetaSiteCookie('1');
            window.location.reload();
        } else {
            clearCookie(cookieNames.useBetaSiteCookie);
            window.location.reload();
        }
    };

    const showSaleCatalogUploadAlert = () => {
        // Show if the user is on a sale catalog page, the event is an upcoming event, only the "Upcoming Auctions" filter is selected, and there are no listings in the event
        return listings?.length == 0 && props.InventoryHeader?.EventID && props.InventoryHeader?.IsUpcomingEvent && (!criteriaData.SelectedFacets || criteriaData.SelectedFacets.length == 0);
    };

    const calculateEstimatedTimes = (eventAverageLotTimeInformation) => {
        const currentListings = listingsRef.current;
        if (currentListings?.length > 0) {
            dispatchListings({
                type: 'update-listings',
                payload: [...currentListings].map(listing => {
                    var eventInfo = eventAverageLotTimeInformation?.find(e => e.EventID === listing.EventID);

                    if (eventInfo && !listing.IsAuctionResult && listing.IsEF) {
                        var estimatedTimeInMinutes = (eventInfo.AverageTimePerLotInMinutes * (listing.CustomSortID - eventInfo.CurrentLiveIndex));
                        if (estimatedTimeInMinutes > 0) {
                            return {
                                ...listing,
                                EstimatedTimeDisplay: getProperTimeFormatFromMinutes(estimatedTimeInMinutes)
                            }
                        }
                    }

                    return {
                        ...listing,
                        EstimatedTimeDisplay: ""
                    }
                })
            });
        }
    }

    return (
        <>
            {showLoadingRing && <LoadingRing sxBox={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, }} />}
            {(searchData.DoubleClickAdCollectionViewModel.ShouldRenderDoubleClickAds && searchData.DoubleClickAdCollectionViewModel.TopPopUpAdvertisement) &&
                <DoubleClickAd
                    {...searchData.DoubleClickAdCollectionViewModel.DoubleClickAdCollection}
                    key={searchData.DoubleClickAdCollectionViewModel.TopPopUpAdvertisement.AdUnitCode}
                    ad={searchData.DoubleClickAdCollectionViewModel.TopPopUpAdvertisement}
                    position={bannerAdPositions.TopPopUp}
                />}
            <CategoryInformation {...searchData.CategoryInformation} IsOtherItemsPage={criteriaData.IsOtherItemsPage} IsAttachmentPage={criteriaData.IsAttachmentAjaxSearch} />
            {showDistanceModal &&
                <DistanceLocationModal {...buildDistanceSortProps()} />}
            {financialCalculatorData.showFinancialCalculator &&
                <CalculatorModal
                    toggleCalculator={closeFinancialCalculator}
                    ShowMonthlyFinanceRatesOnListings={financialCalculatorData.showMonthlyFinanceRatesOnListings}
                    LenderImageUrl={financialCalculatorData.lenderImageUrl}
                    LenderUrl={financialCalculatorData.lenderUrl}
                    LenderCompanyName={financialCalculatorData.lenderCompanyName}
                    Price={financialCalculatorData.currentPrice}
                    Currencies={financialCalculatorData.currencies}
                    DefaultCurrency={props.CurrencySelector.DefaultCurrency}
                    DefaultCultureCode={props.CurrencySelector.DefaultCultureCode}
                    PaymentsAsLowAsTerm={financialCalculatorData.paymentsAsLowAsTerm}
                    PaymentsAsLowAsInterestRate={financialCalculatorData.paymentsAsLowAsInterestRate}
                    LoanApprovalEstimateModel={props.LoanApprovalEstimateModel}
                    ShowExtendedLoanTerms={financialCalculatorData.ShowExtendedLoanTerms}
                />}
            {showAlertPreferenceModal &&
                <AlertPreferenceModal
                    {...notificationSettings}
                    onClose={(e) => { setShowAlertPreferenceModal(false); }}
                    EventID={eventIDForAlertPreferenceModal}
                    EventCategoryID={eventCategoryIDForAlertPreferenceModal}
                />}
            {props.StructuredDataSearchUsed &&
                <input type='hidden' id='gtmsdsf' value={true} />}
            {buyersPremiumData &&
                <BuyersPremiumModal
                    {...buyersPremiumData}
                    setBuyersPremiumData={setBuyersPremiumData}
                />}
            <div className={props.UseNewListPageDesign ? 'list-content list-redesign' : props.IsEvents ? 'list-content extended-width' : 'list-content'} id='toplevellist'>
                <section className='list-top-section'>
                    <div className='list-bcrumbs-and-title'>
                        <div className='ground-bcrumbs'>
                            {!props.IsHostedSite && <MemoComplexBreadcrumb BreadCrumbModels={criteriaData.BreadCrumbs} />}
                            <div className='media-buttons'>
                                {props.ShowPrintThis && !windowSize.isMobile &&
                                    (isEventSaleCatalogPage || props.IsEFEquipmentSearchPage || criteriaData.SearchPageURL ?
                                        <a href={getPrintUrl()} target='_blank' rel='nofollow' className='print-this des-print-this' onClick={() => { gtmPushEvent(gtmEventType.printListDetail); }} target='_blank' disabled={loadImagesForPrint} aria-label={"Print (" + translator.translate('sandhills.opensinanewtab') + ")"}><i className='fas fa-print media-buttons__icon' />
                                        </a>
                                        :
                                        <button id='list-page-print-this' type='button' className='print-this des-print-this ts-button' onClick={onPrint} disabled={loadImagesForPrint}>
                                            <i className='fas fa-print media-buttons__icon' />
                                        </button>
                                    )}
                                {props.ShowEmailThis && !windowSize.isMobile &&
                                    <button id='share' type='button' className='email-this des-email-this' ref={ref} onClick={() => openEmailModal(inquiryType.EmailThis)}>
                                        <i className='fas fa-share-alt media-buttons__icon' />
                                        <span className='visually-hidden'>Share This</span>
                                    </button>}
                            </div>
                        </div>
                        {(searchData.DealerHeaderInfo?.AdditionalEventRings && searchData.DealerHeaderInfo?.AdditionalEventRings.length > 0) &&
                            ((searchData.DealerHeaderInfo?.AdditionalEventRings.length === 1 || !windowSize.isMobile) ?
                                <>
                                    <div className="flex-left-align event-rings">
                                        {searchData.DealerHeaderInfo?.AdditionalEventRings.map(item =>
                                            <a key={item.DisplayText} className={('ts-button ' + (item.CssClass || '')).trim()} href={item.Url}>{item.DisplayText}</a>)}
                                    </div>
                                    <br />
                                </>
                                :
                                <>
                                    <div className="flex-left-align event-rings">
                                        <select onChange={handleRingChange}>
                                            {searchData.DealerHeaderInfo?.AdditionalEventRings.map(item => (
                                                <option key={item.DisplayText} value={item.Url} className={item.CssClass} selected={(item.CssClass && item.CssClass.indexOf("current-event") > 0 ? true : false)}>
                                                    {item.DisplayText}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                        {(props.IsWatchList &&
                            (props.IsWatchlistMyBids ?
                                <>
                                    <div className='watchlist-tabs'>
                                    <div className='watchlist-tab-buttons cf'>
                                        <a className={'watchlist-tab-button watchlist-tab-my-bids-button' + (!props.IsAuctionResult ? ' active' : '')} href={routes.WatchList_MyBids}>{translator.translate('sandhills.currentbids')}</a>
                                        <a className={'watchlist-tab-button watchlist-tab-my-bids-button' + (props.IsAuctionResult && props.IsHighBidder ? ' active' : '')} href={routes.WatchList_MyBids + '?ListingType=Auction%20Results&AuctionListingBidderStatus=HighBidder'}>{translator.translate('sandhills.pastbids')} - {translator.translate('sandhills.highestbid')}</a>
                                        <a className={'watchlist-tab-button watchlist-tab-my-bids-button' + (props.IsAuctionResult && props.IsOutBid ? ' active' : '')} href={routes.WatchList_MyBids + '?ListingType=Auction%20Results&AuctionListingBidderStatus=Outbid'}>{translator.translate('sandhills.pastbids')} - {translator.translate('sandhills.tradepubwebsite.mvc.outbidtext')}</a>
                                        </div>
                                    </div>
                                </> :
                                <>
                                    {props.IsWatchList && props.UseAuctionWatchList ? (
                                        <div className='watchlist-tabs'>
                                            <div className='watchlist-tab-buttons cf'>
                                                <a className={'watchlist-tab-button' + (!props.IsRecommendedListingsSearch && !props.IsRecommendedAuctionsSearch && props.IsEvents && !props.IsAuctionResult ? ' active' : '')} href={routes.EventsWatchList}>{translator.translate('sandhills.tradesites.mvc.watchedauctions')}</a>
                                                <a className={'watchlist-tab-button' + (!props.IsRecommendedListingsSearch && props.IsEvents && props.IsAuctionResult ? ' active' : '')} href={routes.EventsWatchList + '?eventType=past'}>{translator.translate('sandhills.tradesites.mvc.pastauctions')}</a>
                                                <a className={'watchlist-tab-button' + (!props.IsRecommendedListingsSearch && !props.IsEvents && !props.IsAuctionResult ? ' active' : '')} href={routes.WatchList}>{translator.translate('sandhills.tradesites.mvc.watchedauctionlots')}</a>
                                                <a className={'watchlist-tab-button' + (!props.IsRecommendedListingsSearch && !props.IsEvents && props.IsAuctionResult ? ' active' : '')} href={routes.WatchList + '?ListingType=Auction%20Results'}>{translator.translate('sandhills.tradesites.mvc.pastauctionlots')}</a>
                                                {props.ShowRecommendedListingsToUser &&
                                                    <a className={'watchlist-tab-button' + (props.IsRecommendedListingsSearch ? ' active' : '')} href={routes.WatchList + '?RecommendedListingsSearch=1'}>{translator.translate('sandhills.recommendedforyou')}</a>}
                                                {props.ShowRecommendedAuctionsToUser &&
                                                    <a className={'watchlist-tab-button' + (props.IsRecommendedAuctionsSearch && props.IsEvents ? ' active' : '')} href={routes.EventsWatchList + '?RecommendedAuctionsSearch=1'}>{translator.translate('sandhills.auctionsforyou')}</a>}
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {props.IsWatchList &&
                                                <div className='watchlist-tabs half-width'>
                                                    <div className='watchlist-tab-buttons cf'>
                                                        <a className={'watchlist-tab-button' + (!props.IsRecommendedListingsSearch && !props.IsRecommendedAuctionsSearch ? ' active' : '')} href={routes.WatchList}>Watched Listings</a>
                                                        {props.ShowRecommendedListingsToUser &&
                                                            <a className={'watchlist-tab-button' + (props.IsRecommendedListingsSearch ? ' active' : '')} href={routes.WatchList + (routes.WatchList.includes('?') ? '&' : '?') + 'RecommendedListingsSearch=1'}>{translator.translate('sandhills.recommendedforyou')}</a>}
                                                        {props.ShowRecommendedAuctionsToUser &&
                                                            <a className={'watchlist-tab-button' + (props.IsRecommendedAuctionsSearch && props.IsEvents ? ' active' : '')} href={routes.EventsWatchList + '?RecommendedAuctionsSearch=1'}>{translator.translate('sandhills.auctionsforyou')}</a>}
                                                    </div>
                                                </div>
                                            }
                                        </>
                                    )}
                                </>
                            )
                        )}


                        {(!props.IsHostedSite && searchData.DealerHeaderInfo) && !searchData.DealerHeaderInfo?.EventID && !props.IsBothEventTypesDealerPage &&
                            <div className='dealer-tabs'>
                                <div className='dealer-tab-buttons cf'>
                                    {searchData.DealerHeaderInfo.InventoryUrls && searchData.DealerHeaderInfo.InventoryUrls.length > 0 && searchData.DealerHeaderInfo.InventoryUrls.map(item =>
                                        <a key={item.DisplayText} className={'dealer-tab-button' + ((item.Url.includes('past') && criteriaData.SearchBaseURL.includes('past')) || (item.Url.includes('upcoming') && criteriaData.SearchBaseURL.includes('upcoming')) ? ' active' : item.CssClass ? " " + item.CssClass : "")} href={item.Url}>{item.DisplayText}</a>)}
                                </div>
                            </div>}
                        {isFTISearch &&
                            <img className='fti-img' src={routes.CDNBaseUrl + 'Images/Logos/FastTrack/fti-logo.png'} alt='FastTrackIron' />
                        }
                        {isRentalSearch && !currentSite?.Site?.IsController && !currentSite?.Site?.IsEquineMarket && (currentSite?.Site?.IsDotCom || currentSite?.Site?.IsDotCa) &&
                            <img className='rentalyard-img' src={routes.CDNBaseUrl + 'Images/Logos/RentalYard/RY-logo.png'} alt='RentalYard' />
                        }
                        <div className='list-heading'>
                            {windowSize.isMobile && props.UseNewListPageDesign ?
                                <>
                                    <span className='title-group'>
                                        <span className='list-title-left'>
                                            <h1 className='list-title'>
                                                <span className='list-title-text'>{isFTISearch && translator.translate('sandhills.crm.dscrm.wholesale') + ' '}{criteriaData.PageHeader}</span>
                                            </h1>
                                            {props.CustomMetaDataApplied && props.ShowEmployeeOnlyInfo &&
                                                <a aria-label={translator.translate('sandhills.viewcustommetadata')} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleEmployeeCustomMetadataModal(); } }} onClick={toggleEmployeeCustomMetadataModal}>
                                                    <i className='fas fa-external-link-alt'></i>
                                                </a>}
                                            {showEmployeeCustomMetadataModal &&
                                                <EmployeeCustomMetadataModal {...props.EmployeeOnlyCustomMetadata} toggleEmployeeCustomMetadataModal={toggleEmployeeCustomMetadataModal}/>}
                                            <strong className='list-listings-count'>&nbsp;{props.UserCanViewListings ? searchData.ListingCountTitle : ''}</strong>
                                        </span>
                                    </span>
                                    <span className='toggle-container'>
                                        {(props.EnableWebSavedSearches && !props.IsWatchList && windowSize.isMobile) &&
                                            <WebSavedSearchesButton {...savedSearchData} IsDefaultSearch={criteriaData.IsDefaultSearch} TurnOffDefaultSaveThisSearch={criteriaData.TurnOffDefaultSaveThisSearch}
                                                openModal={openSavedSearchModal} closeModal={closeSavedSearchModal}
                                                getWebSavedSearches={getWebSavedSearches} removeWebSavedSearch={removeWebSavedSearch}
                                                saveCurrentSearch={saveCurrentSearch} updateWebSavedSearch={updateWebSavedSearch} setPendingSave={setPendingSave} />
                                        }
                                        <ListingOptions
                                            setShowAllSpecs={setShowAllSpecs}
                                            setExpandAllDescriptions={setExpandAllDescriptions}
                                            showAllSpecs={showAllSpecs}
                                            showFullDescription={expandAllDescriptions}
                                            isMobile
                                            IsAuthenticated={currentUser.IsAuthenticated}
                                        />
                                        <span className='toggle-contain'>
                                            <div className='page-type-toggle'>
                                                <div id='list-toggle' aria-label={translator.translate('sandhills.listview')} title={translator.translate('sandhills.listview')} className={newView ? 'sort' : 'sort active'} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleView(); } }} onClick={toggleView}>
                                                    <i className='fas fa-list' />
                                                </div>
                                                <div id='grid-toggle' aria-label={translator.translate('sandhills.thumbnailview')} title={translator.translate('sandhills.thumbnailview')} className={newView ? 'sort active' : 'sort'} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleView(); } }} onClick={toggleView}>
                                                    <i className='fas fa-table-cells-large' />
                                                </div>
                                            </div>
                                        </span>
                                    </span>
                                </>
                                :
                                <>
                                    {(isEventSaleCatalogPage ?
                                        <h2 className='list-title'><span className='list-title-text'>{criteriaData.PageHeader}</span></h2>
                                        :
                                        <h1 className='list-title'><span className='list-title-text'>{isFTISearch && translator.translate('sandhills.crm.dscrm.wholesale') + ' '}{criteriaData.PageHeader}</span></h1>
                                    )}
                                    {props.CustomMetaDataApplied && props.ShowEmployeeOnlyInfo &&
                                        <a aria-label={translator.translate('sandhills.viewcustommetadata')} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleEmployeeCustomMetadataModal(); } }} onClick={toggleEmployeeCustomMetadataModal}>
                                            <i className='fas fa-external-link-alt'></i>
                                        </a>}
                                    {showEmployeeCustomMetadataModal &&
                                        <EmployeeCustomMetadataModal {...props.EmployeeOnlyCustomMetadata} toggleEmployeeCustomMetadataModal={toggleEmployeeCustomMetadataModal} />}
                                    <strong className='list-listings-count'>&nbsp;{props.UserCanViewListings ? searchData.ListingCountTitle : ''}</strong>
                                    <div className="listing-option-bar-dropdowns result-dropdown">
                                        {showNumResultsDisplayedSelect &&
                                            <ResultsDropdown
                                                location={"top"}
                                                OnChange={onResultsChange}
                                                SelectedValue={searchData.PageSize}
                                                UseNewListPageDesign={props.UseNewListPageDesign}
                                            />}
                                    </div>
                                    {searchData.HighLowAverageURL && !currentSite?.Site?.IsRentalYard ?
                                        <a id='list-page-hi-low-button' className='high-low-average-button' href={searchData.HighLowAverageURL} content="noindex" rel="nofollow">
                                            <i className='fas fa-long-arrow-alt-up' /> <i className='fas fa-long-arrow-alt-down' /> <TranslationText tKey='sandhills.tradepubwebsite.mvc.hilowaverage' />
                                        </a>
                                        :
                                        (props.StolenEquipmentVendorUrl &&
                                            <a id='list-page-post-stolen-listing-button' className='high-low-average-button' href={props.StolenEquipmentVendorUrl}>
                                                <TranslationText tKey='sandhills.postastolenlisting' />
                                            </a>)}
                                    {props.ShowViewAllLots &&
                                        <a id='list-page-view-all-lots-button' className='high-low-average-button' href={`/listings/search?EventCategoryID=8&ListingType=Upcoming%20Auctions&ScopeCategoryIDs=${currentSite?.Site?.IsEquineMarket ? '171076' : '171000'}`}>
                                            <TranslationText tKey='sandhills.viewalllots' />
                                        </a>
                                    }
                                </>
                            }
                        </div>
                        {(criteriaData.PriceBlurb || searchData.DynamicBlurb) && !searchData.ShowBlurb && !windowSize.isMobile && <div className={searchData.DynamicBlurb ? 'price-blurb dynamic-blurb' : 'price-blurb'}><span dangerouslySetInnerHTML={{ __html: (criteriaData.PriceBlurb ?? searchData.DynamicBlurb) }} /></div>}
                        {(!props.IsHostedSite && searchData.ShowBlurb) && !windowSize.isMobile &&
                            <div className='product-blurb-summary'>
                                <h5>{props.BlurbModel.Title}</h5>
                                <p dangerouslySetInnerHTML={{ __html: props.BlurbModel.SummaryPlainText }}></p>
                                {props.BlurbModel.MainBodyHTML && <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); scrollToBlurb(); } }} id='list-page-readmore' className='read-more' onClick={scrollToBlurb}><TranslationText tKey='sandhills.readmore' /> <span className='visually-hidden' >({props.BlurbModel.Title})</span></a>}
                            </div>}
                        {!props.IsHostedSite && searchData.ShowBlurb && windowSize.isMobile &&
                            <div className='mobile-product-blurb-summary'>
                                <p dangerouslySetInnerHTML={{ __html: truncateString(props.BlurbModel.SummaryPlainText, 100) }}></p>
                                {props.BlurbModel.MainBodyHTML && <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); scrollToBlurb(); } }} id='list-page-readmore' className='read-more' onClick={scrollToBlurb}><TranslationText tKey='sandhills.readmore' /> <span className='visually-hidden' >({props.BlurbModel.Title})</span></a>}
                            </div>
                        }
                        {(criteriaData.PriceBlurb || searchData.DynamicBlurb) && !searchData.ShowBlurb && windowSize.isMobile &&
                            <div className={searchData.DynamicBlurb ? 'mobile-product-blurb-summary price-blurb dynamic-blurb' : 'mobile-product-blurb-summary price-blurb'}>
                                <span dangerouslySetInnerHTML={{ __html: (truncateString(criteriaData.PriceBlurb, 100) ?? searchData.DynamicBlurb.slice(0, dynamicBlurbMobileSplitIndex + 1)) }} />
                                <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); scrollToBlurb(); } }} id='list-page-readmore' className='read-more' onClick={scrollToBlurb}><TranslationText tKey='sandhills.readmore' /></a>
                            </div>}

                        {windowSize.isMobile &&
                            props.ValidTimeZones && <>
                                <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowTimeZoneModal(true); } }} className='time-zone-container'>
                                    <TranslationText tKey='sandhills.crm.dscrm.timezone' />: <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowTimeZoneModal(true); } }} className='time-zone' onClick={(e) => setShowTimeZoneModal(true)}><i className='fas fa-globe-americas' />&nbsp;{currentUser.UserTimeZone}</div>
                                </div>
                            </>}

                        {windowSize.isMobile && shouldShowQuickBidToggle() && renderQuickBidToggle()}

                        {(criteriaData.SelectedSortValue === searchData.distanceSortID) &&
                            <div className='distance-sort-descriptor'>
                                <p className='distance-from'>
                                    <TranslationText tKey='sandhills.distancefromterm' replaceValues={['']} />
                                    <span className='distance-nearest-sort'>{searchData.LocationString}</span>
                                    <button className='distance-sort-update' onClick={onDistanceLocationModalTouch}>
                                        <TranslationText tKey='sandhills.update' />
                                    </button>
                                </p>
                            </div>}
                        {currentSite?.Site?.IsCharterHub && !windowSize.isMobile && !props.IsWatchList &&
                            <a id='request-a-charter-button' className='request-a-charter-button' onClick={onRequestAQuoteModalToggle}>
                                <i className='fas fa-plane-departure' /><h2 className='rac-text'>{translator.translate('sandhills.requestacharterquote')}</h2>
                            </a>
                        }
                    </div>
                    {windowSize.isNotMobile &&
                        <div id="listing-option-bar-container" className='listing-option-bar-div non-sticky'>
                            <div className='listing-option-bar'>
                                <div className='listing-option-bar-content'>
                                    <span className='list-listings-count'>{searchData.ListingCountTitle}</span>
                                    {(selectedCompareListings && selectedCompareListings.length > 0) &&
                                        <div className='compare-selected-listings-container has-compare-listings'>
                                            <a id='list-page-compare-button' className='compare-button-list des-compare-button' href={props.CompareUrl}>
                                                <TranslationText tKey='sandhills.compare' />
                                                {'\u00A0'}
                                                <span id='comp-img-button-count'>
                                                    {selectedCompareListings ? selectedCompareListings.length : '0'} / 4
                                                </span>
                                            </a>
                                            <div className='selected-compare-images'>
                                                {selectedCompareListings.map((compareListing, index) =>
                                                    <div key={'CompareThumbnail' + compareListing.ListingID} className='hover-close' id={compareListing.ListingID} onClick={() => updateCompareListing(compareListing.ListingID, null, false)}>
                                                        <i className='fas fa-times' />
                                                        <img src={compareListing.ImageUrl} alt={'Compare Image_' + (index + 1)} />
                                                    </div>)}
                                            </div>
                                        </div>}
                                    <span className='hide-sticky '>
                                        {stickyHeaderSet && !props.IsHostedSite &&
                                            <QuickFind {...props} isStickyHeader={true} container={'.listing-option-bar'} defaultText={<span className='capitalize'><TranslationText tKey='sandhills.startanewsearch' /></span>} />}

                                        {props.StolenEquipmentVendorUrl &&
                                            <a id='list-page-post-stolen-listing-button' className='high-low-average-button' href={props.StolenEquipmentVendorUrl}>
                                                <TranslationText tKey='sandhills.postastolenlisting' />
                                            </a>}
                                        {props.ShowViewAllLots &&
                                            <a id='list-page-view-all-lots-button' className='high-low-average-button' href={`/listings/search?EventCategoryID=8&ListingType=Upcoming%20Auctions&ScopeCategoryIDs=${currentSite?.Site?.IsEquineMarket ? '171076' : '171000'}`}>
                                                <TranslationText tKey='sandhills.viewalllots' />
                                            </a>
                                        }
                                        {(searchData.SortOrderItems && searchData.SortOrderItems.length > 0) &&
                                            <SortDropdown
                                                IsDesktop={!mobileSearch}
                                                SortOptions={searchData.SortOrderItems}
                                                OnChange={onSortChange}
                                                SelectedValue={criteriaData.SelectedSortValue > 0 ? criteriaData.SelectedSortValue : searchData.defaultSortID}
                                                SortButton={true}
                                                ShowMobileSort={showMobileSort}
                                                toggleMobileSort={toggleMobileSort}
                                            />
                                        }
                                    </span>
                                </div>
                                {useBetaSiteCookie && showEmployeeOnlyBetaSiteToggle &&
                                    <div className='listing-option-bar'>
                                        <BetaSiteCookieWarning />
                                    </div>}
                                {(criteriaData.SelectedSortValue === searchData.distanceSortID) &&
                                    <div className='distance-sort-descriptor'>
                                        <p className='distance-from'>
                                            <TranslationText tKey='sandhills.distancefromterm' replaceValues={['']} />
                                            <span className='distance-nearest-sort'>{searchData.LocationString}</span>
                                            <button className='distance-sort-update' onClick={onDistanceLocationModalTouch}>
                                                <TranslationText tKey='sandhills.update' />
                                            </button>
                                        </p>
                                    </div>}
                            </div>
                        </div>
                    }

                    {windowSize.width <= 785 && !props.IsHostedSite &&
                        <div id="listing-option-bar-container" className='listing-option-bar-div non-sticky'>
                            <div className='listing-option-bar'>
                                {stickyHeaderSet && <QuickFind {...props} isStickyHeader={true} container={'.listing-option-bar'} defaultText={<span className='capitalize'><TranslationText tKey='sandhills.startanewsearch' /></span>} />}
                            </div>
                        </div>
                    }
                </section>
                <section className='bottom-section'>
                    <div className='side-container'>
                        {props.ValidTimeZones && !windowSize.isMobile && <>
                            <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') e.preventDefault(); setShowTimeZoneModal(true); }} className='time-zone-container'>
                                <TranslationText tKey='sandhills.crm.dscrm.timezone' />: <div className='time-zone' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowTimeZoneModal(true); } }} onClick={(e) => setShowTimeZoneModal(true)}><i className='fas fa-globe-americas' />&nbsp;{currentUser.UserTimeZone}</div>
                            </div>
                        </>}
                        {showTimeZoneModal && props.ValidTimeZones &&
                            <TimeZoneModal options={props.ValidTimeZones} onClose={(e) => { setShowTimeZoneModal(!showTimeZoneModal) }} />
                        }

                        {/* If you are adding or changing any props here, make sure that you reflect those changes in the memoFacetedSearch comparator function */}
                        {showFilter() &&
                            <MemoFacetedSearch
                                SearchFields={memoSearchFields}
                                SelectedFacets={memoSelectedFacets}
                                SortOrderItems={memoSortOrderItems}
                                IsDefaultSearch={criteriaData.IsDefaultSearch}
                                SelectedSortValue={criteriaData.SelectedSortValue}
                                ListingsCount={searchData.ListingsCount}
                                runSearch={runSearch}
                                clearAll={clearAll}
                                clearFacet={clearFacetDebounced}
                                disabled={criteriaSearchInProgress}
                                showMobileFilters={showMobileFilters}
                                toggleMobileFilter={toggleMobileFilter}
                                mobileSearch={mobileSearch}
                                isDealerInventoryLandingPage={props.IsDealerInventoryLandingPage}
                                openFacetedModalOnLoad={openFacetedModalOnLoad}
                                IsWatchList={props.IsWatchList}
                                useNextUpFacets
                                EnableWebSavedSearches={props.EnableWebSavedSearches && !props.IsWatchList}
                                savedSearchData={savedSearchData}
                                openSavedSearchModal={openSavedSearchModal}
                                closeSavedSearchModal={closeSavedSearchModal}
                                getWebSavedSearches={getWebSavedSearches}
                                removeWebSavedSearch={removeWebSavedSearch}
                                saveCurrentSearch={saveCurrentSearch}
                                updateWebSavedSearch={updateWebSavedSearch}
                                EmptyDealerLandingPageEnabledFacets={props.EmptyDealerLandingPageEnabledFacets}
                                isAttachmentPage={criteriaData.IsAttachmentAjaxSearch}
                                IsEventsPage={props.IsEvents}
                                setPendingSave={setPendingSave}
                                TurnOffDefaultSaveThisSearch={criteriaData.TurnOffDefaultSaveThisSearch}
                                KeywordSearchParamsDetected={criteriaData?.KeywordSearchParamsDetected}
                                ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                                setShowLoadingRing={setShowLoadingRing}
                                listingType={listingType}
                                distanceSortID={searchData.distanceSortID}
                                SearchPageURL={criteriaData.SearchPageURL}
                                SearchBaseURL={criteriaData.SearchBaseURL}
                                PendingSearchCriteria={criteriaData.PendingSearchCriteria}
                                ClearAllURL={criteriaData.ClearAllURL}
                                highlightSelectedFacets={highlightSelectedFacets}
                                onHighlightEnd={() => setHighlightSelectedFacets(false)}
                            />
                        }

                        {!windowSize.isMobile && criteriaData.RelevantPartsLink && <MemoRelevantPartsLink RelevantPartsTitle={criteriaData.RelevantPartsTitle} RelevantPartsLink={criteriaData.RelevantPartsLink} />}
                        {!windowSize.isMobile && props.Blogs?.length > 0 && <MemoListPageBlogs blogs={props.Blogs} BlogTitle={props.BlogTitle} />}
                        {!windowSize.isMobile && currentUser.CanViewFTI && <MemoFTIWTBPlug />}
                        {!windowSize.isMobile && showFilter() &&
                            searchData.DoubleClickAdCollectionViewModel.ShouldRenderDoubleClickAds &&
                            searchData.DoubleClickAdCollectionViewModel.FacetedSearchAdvertisement &&
                            <DoubleClickAd
                                {...memoDoubleClickAdCollection}
                                key={searchData.DoubleClickAdCollectionViewModel.FacetedSearchAdvertisement.AdUnitCode}
                                ad={searchData.DoubleClickAdCollectionViewModel.FacetedSearchAdvertisement}
                                position={bannerAdPositions.FacetedSearch}
                                contained
                            />}
                    </div>
                    {!windowSize.isMobile && shouldShowQuickBidToggle() && renderQuickBidToggle()}
                    <div id='main-content' className={'list-main-section ' + (showFilter() ? 'has-search-bar ' : 'no-search-bar ') + (props.IsWatchList ? 'watchlist-section' : '')}>
                        <h2 className='visually-hidden'>{props.UserCanViewListings ? searchData.ListingCountTitle : translator.translate('sandhills.tradepubwebsite.mvc.listings')}</h2>
                        {!windowSize.isMobile && currentSite?.Site?.IsCharterHub && !props.IsWatchList && !props.IsDealerInventoryLandingPage && <LocationMap locations={searchData.ListingLocations} listings={listings} listingMapData={searchData.ListingMapData} onLocationChange={setLocationState} defaultCenter={searchData.DefaultCenter} defaultZoom={searchData.DefaultZoom} disableAutoCentering={!searchData.DefaultCenter} ref={locationMapRef} isOpen={true} currentUser={currentUser} loading={mapDataLoading} />}
                        {props.ShowMarketAnalysis && <MarketAnalysis {...searchData.MarketAnalysis} MarketAlanysisURL={marketAnalysisURL} ShowChart={toggleCloseMA} reloadChart={forceReloadMA} setShowChart={setToggleCloseMA} />}
                        {(props.IsHostedSite || props.IsFramelessOrHostedSite || props.IsStolenEquipment) ? renderSCFBarPlaceholder() :                            
                            <MemoShowClosestFirstToggle {...distanceSortProps}
                                // If you are adding or changing any props here, make sure that you reflect those changes in the memoShowClosestFirstToggle comparator function
                                IsFTISearch={isFTISearch}
                                IsRentalSearch={isRentalSearch}
                                IsFractionalSearch={isFractionalSearch}
                                IsOnlineAuctionSearch={isOnlineAuctionSearch}
                                IsOnSiteAuctionSearch={isOnSiteAuctionSearch}
                                IsDismantledSearch={isDismantledSearch}
                                toggleFTISearch={toggleFTISearch}
                                RentalToggleText={props.RentalToggleText}
                                setUseGridView={changeGridView}
                                UseNewListPageDesign={useGridView}
                                CanViewFTI={currentUser.CanViewFTI}
                                DisplayShowClosestFirstByConsent={searchData.DisplayShowClosestFirstByConsent}
                                IsListingsPage={!props.IsEvents}
                                SortOrderItems={searchData.SortOrderItems}
                                IsDesktop={!mobileSearch}
                                OnChange={onSortChange}
                                SelectedValue={criteriaData.SelectedSortValue > 0 ? criteriaData.SelectedSortValue : searchData.defaultSortID}
                                SortButton={true}
                                ShowMobileSort={showMobileSort}
                                toggleMobileSort={toggleMobileSort}
                                displaySCFInput={displaySCFInput}
                                setDisplaySCFInput={setDisplaySCFInput}
                                AuctionResultsPage={criteriaData.IsAuctionResults}
                                setShowDistanceModal={setShowDistanceModal}
                                setShowAllSpecs={setShowAllSpecs}
                                setExpandAllDescriptions={setExpandAllDescriptions}
                                showAllSpecs={showAllSpecs}
                                showFullDescription={expandAllDescriptions}
                                ListingsToggles={props.ListingsToggles}
                                IsEventCatalog={isEventSaleCatalogPage || props.IsConsignmentPackageListPage || props.IsStandaloneSale}
                                IsWatchListMyBids={props.IsWatchlistMyBids}
                                IsWatchList={props.IsWatchList}
                                requestLocationOnMount={props.RequestLocationOnMount}                                
                            />}
                        {quickBiddingEnabled && shouldShowQuickBidToggle() && renderBidAgreementBlurb()}
                        {props?.IsExclusiveAuctions && renderExclusiveAuctionsBar()}
                        {searchData.DealerHeaderInfo &&
                            <AdditionalPageInformation
                                {...searchData.DealerHeaderInfo}
                                isEvents={props.IsEvents}
                                isConsignmentPackage={props.IsConsignmentPackageListPage}
                                isStandaloneSale={props.IsStandaloneSale}
                                saleHasBidAgreement={props.SaleHasBidAgreement}
                                openEmailModal={openEmailModal}
                                openBiddingAgreementModal={openBiddingAgreementModal}
                                ref={ref} />
                        }
                        {searchData.DoubleClickAdCollectionViewModel.ShouldRenderDoubleClickAds &&
                            <>
                                {searchData.DoubleClickAdCollectionViewModel.TopAdvertisement && !similarListings?.length &&
                                    <div className='top-banner-ad-container'>
                                        <DoubleClickAd
                                            {...memoDoubleClickAdCollection}
                                            key={searchData.DoubleClickAdCollectionViewModel.TopAdvertisement.AdUnitCode}
                                            ad={searchData.DoubleClickAdCollectionViewModel.TopAdvertisement}
                                            style={useGridView ? { marginBottom: 0 } : undefined}
                                            position={bannerAdPositions.Top}
                                            contained
                                        />
                                    </div>}
                                {searchData.DoubleClickAdCollectionViewModel.AuctionResultsAdvertisement &&
                                    <div className='top-banner-ad-container'>
                                        <DoubleClickAd
                                            {...memoDoubleClickAdCollection}
                                            key={searchData.DoubleClickAdCollectionViewModel.AuctionResultsAdvertisement.AdUnitCode}
                                            ad={searchData.DoubleClickAdCollectionViewModel.AuctionResultsAdvertisement}
                                            style={useGridView ? { marginBottom: 0 } : undefined}
                                            position={bannerAdPositions.AuctionResults}
                                            contained
                                        />
                                    </div>}
                            </>}
                        {(((listings && listings.length > 0) || (props.IsWatchList && searchData.TotalPages > 0)) && props.UserCanViewListings) ?
                            <>
                                {(props.IsBothEventTypesDealerPage) ?
                                    <>
                                        {liveListings.length > 0 &&
                                            <>
                                                <h3 class="auction-type-banner-heading">{translator.translate('sandhills.upcomingauctions')}</h3>
                                                <div className={'list-container ' + (CardType !== 'WEBLISTING' ? 'list-container-flexrow' : '')} id='listContainer'>
                                                    {liveListings.map((listing, index) =>
                                                        <Fragment key={(listing.Id + '-' + index)}>
                                                            {listing.GroupBanner?.DisplayGroup == 'WorldWide' && useGridView &&
                                                                <>
                                                                    <LocalizedSimilarListings {...searchData.LocalizedSimilarListingsInfo} useGridView={useGridView} />
                                                                    <ListingBanner {...listing.GroupBanner} />
                                                                </>}
                                                            {listing.GroupBanner?.DisplayGroup == 'WorldWide' && !useGridView && <LocalizedSimilarListings {...searchData.LocalizedSimilarListingsInfo} useGridView={useGridView} />}
                                                            <div className={((CardType !== 'WEBLISTING' && CardType !== 'BOXED') ? 'list-listing-card-wrapper' : 'list-listing-wrapper')}>
                                                                {renderListing(listing, index)}
                                                            </div>
                                                            {renderBannerAd(index)}
                                                            {(index == loanApprovalBannerIndex || listings.length == 1 || (useGridView && listings.length == 2 && index == 1)) && props.LoanApprovalEstimateModel.ShowBanner &&
                                                                <LoanApprovalEstimateBanner {...props.LoanApprovalEstimateModel} pageType='list' />}
                                                            {searchData.CurrentPage == 1 && (index == 3 || listings.length == 1) && props.AuctionResultsYouTubePlaylistID &&
                                                                <EmbeddedYouTubePlaylist descriptionText={translator.translate('sandhills.viewauctionresultsfromauctiontime')} playlistId={props.AuctionResultsYouTubePlaylistID} />}
                                                        </Fragment>)}
                                                </div>
                                            </>
                                        }
                                        {pastListings.length > 0 &&
                                            <>
                                                <h3 class="auction-type-banner-heading">{translator.translate('sandhills.tradepubwebsite.mvc.auctionresults')}</h3>
                                                <div className={'list-container ' + (CardType !== 'WEBLISTING' ? 'list-container-flexrow' : '')} id='listContainer'>
                                                    {pastListings.map((listing, index) => {
                                                        // Need to adjust so banner ads & lazy load account for upcoming & past being separate lists
                                                        index = index + liveListings.length;

                                                        return (
                                                            <Fragment key={(listing.Id + '-' + 0)}>
                                                                {listing.GroupBanner?.DisplayGroup == 'WorldWide' && useGridView &&
                                                                    <>
                                                                        <LocalizedSimilarListings {...searchData.LocalizedSimilarListingsInfo} useGridView={useGridView} />
                                                                        <ListingBanner {...listing.GroupBanner} />
                                                                    </>}
                                                                {listing.GroupBanner?.DisplayGroup == 'WorldWide' && !useGridView && <LocalizedSimilarListings {...searchData.LocalizedSimilarListingsInfo} useGridView={useGridView} />}
                                                                <div className={((CardType !== 'WEBLISTING' && CardType !== 'BOXED') ? 'list-listing-card-wrapper' : 'list-listing-wrapper')}>
                                                                    {renderListing(listing, index)}
                                                                </div>
                                                                {renderBannerAd(index)}
                                                                {(index == loanApprovalBannerIndex || listings.length == 1 || (useGridView && listings.length == 2 && index == 1)) && props.LoanApprovalEstimateModel.ShowBanner &&
                                                                    <LoanApprovalEstimateBanner {...props.LoanApprovalEstimateModel} pageType='list' />}
                                                                {searchData.CurrentPage == 1 && (index == 3 || listings.length == 1) && props.AuctionResultsYouTubePlaylistID &&
                                                                    <EmbeddedYouTubePlaylist descriptionText={translator.translate('sandhills.viewauctionresultsfromauctiontime')} playlistId={props.AuctionResultsYouTubePlaylistID} />}
                                                            </Fragment>
                                                        );

                                                    })}
                                                </div>
                                            </>
                                        }
                                        {displayPagination(searchData) &&
                                            <Paging
                                                currentPage={searchData.CurrentPage}
                                                totalPages={searchData.TotalPages}
                                                onChange={handlePageNumberChange}
                                            />}

                                    </>
                                    :
                                    <>
                                        <div className={'list-container ' + (CardType !== 'WEBLISTING' ? 'list-container-flexrow' : '')} id='listContainer'>
                                            {listings.map((listing, index) =>
                                                <Fragment key={(listing.Id + '-' + index)}>
                                                    {index === worldWideBannerIndex && !currentSite.Site.IsControllerEMEA &&
                                                        <>
                                                            <LocalizedSimilarListings {...searchData.LocalizedSimilarListingsInfo} useGridView={useGridView} />
                                                            <ListingBanner {...listings[worldWideBannerListingIndex].GroupBanner} />
                                                        </>
                                                    }
                                                    {listing.GroupBanner?.DisplayGroup == 'WorldWide' && !useGridView && <LocalizedSimilarListings {...searchData.LocalizedSimilarListingsInfo} useGridView={useGridView} />}
                                                    <div className={((CardType !== 'WEBLISTING' && CardType !== 'BOXED') ? 'list-listing-card-wrapper' : 'list-listing-wrapper')}>
                                                        {renderListing(listing, index)}
                                                    </div>
                                                    {(index === 3 || (listings.length < 4 && index + 1 === listings.length)) && ftiWTBs?.length > 0 &&
                                                        <FTISlider ftis={ftiWTBs} />
                                                    }
                                                    {renderBannerAd(index)}
                                                    {(index == loanApprovalBannerIndex || listings.length == 1 || (useGridView && listings.length == 2 && index == 1)) && props.LoanApprovalEstimateModel.ShowBanner &&
                                                        <LoanApprovalEstimateBanner {...props.LoanApprovalEstimateModel} pageType='list' />}
                                                    {index === RVWizardBannerIndex && currentSite.Site.IsRVUniverse &&
                                                        <WizardBanner IsListPage={true} WizardURL={props.WizardURL} />}
                                                    {searchData.CurrentPage == 1 && (index == 3 || listings.length == 1) && props.AuctionResultsYouTubePlaylistID &&
                                                        <EmbeddedYouTubePlaylist descriptionText={translator.translate('sandhills.viewauctionresultsfromauctiontime')} playlistId={props.AuctionResultsYouTubePlaylistID} />}
                                                </Fragment>)}
                                        </div>
                                        <div className={"list-bottom-controls"}>
                                            {displayPagination(searchData) &&
                                                <Paging
                                                    currentPage={searchData.CurrentPage}
                                                    totalPages={searchData.TotalPages}
                                                    onChange={handlePageNumberChange}
                                                />}
                                            {showNumResultsDisplayedSelect &&
                                                <ResultsDropdown
                                                    location={"mobile"}
                                                    OnChange={onResultsChange}
                                                    SelectedValue={searchData.PageSize}
                                                    UseNewListPageDesign={props.UseNewListPageDesign}
                                                />}
                                        </div>
                                    </>}
                            </>
                            :
                            showSaleCatalogUploadAlert() ?
                                <div className='event-empty-catalog-container'>
                                    <div className='event-empty-catalog'>
                                        <div className='title'>{translator.translate('sandhills.auctioncatalogcomingsoon')}</div>
                                        <div className='message'><span class='link' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter') openAlertPreferenceModal(props.InventoryHeader?.EventID, props.InventoryHeader?.EventCategoryID) }} onClick={() => openAlertPreferenceModal(props.InventoryHeader?.EventID, props.InventoryHeader?.EventCategoryID)}>{translator.translate('sandhills.getnotified')}</span> {translator.translate('sandhills.whenthecatalogisavailable')}</div>
                                    </div>
                                </div>
                                :
                                <div className='list-container'>
                                    <div className='list-error'>
                                        {renderError()}
                                    </div>
                                    {searchData.SimilarListingsModel && similarListings?.length > 0 &&
                                        <div>
                                            <div className='list-container-flexrow'>
                                                {similarListings.map((value, index) => (
                                                    <Fragment key={value.Id + '-' + index}>
                                                        <div className='list-listing-card-wrapper similar-listings' >
                                                            {renderListing(value, index, true)}
                                                        </div>
                                                        {renderBannerAd(index)}
                                                        {(index == loanApprovalBannerIndex || listings.length == 1 || (useGridView && listings.length == 2 && index == 1)) && props.LoanApprovalEstimateModel.ShowBanner &&
                                                            <LoanApprovalEstimateBanner {...props.LoanApprovalEstimateModel} pageType='list' />}
                                                    </Fragment>
                                                ))}
                                            </div>
                                            {searchData.SimilarListingsModel.ViewMoreUrl &&
                                                <div className="centered-div-and-text similar-view-more-button">
                                                    <a href={searchData.SimilarListingsModel.ViewMoreUrl} onClick={() => gtmPushEvent(gtmEventType.noListingsViewMoreClick)} aria-label="view more similiar listings" className="ts-button" >{translator.translate('sandhills.viewmore').toString()}</a>
                                                </div>}
                                        </div>
                                    }
                                </div>}
                    </div>
                    <div className='clear' />

                    {!props.IsHostedSite && searchData.ShowBlurb && windowSize.isMobile &&
                        <div className='full-text-blurb '>
                            <p dangerouslySetInnerHTML={{ __html: props.BlurbModel.SummaryPlainText }}></p>
                        </div>
                    }
                    {searchData.ShowBlurb &&
                        <div className='full-text-blurb has-no-blog' id='full-text' ref={listBlurbRef}>
                            <div className='inner-blurb-wrapper'>
                                <div dangerouslySetInnerHTML={{ __html: props.BlurbModel.MainBodyHTML }} />
                            </div>
                        </div>}
                    {(criteriaData.PriceBlurb || searchData.DynamicBlurb) && !searchData.ShowBlurb && windowSize.isMobile &&
                        <div id='full-text' ref={listBlurbRef} className='full-text-blurb'><span dangerouslySetInnerHTML={{ __html: (criteriaData.PriceBlurb ?? searchData.DynamicBlurb) }} /></div>}
                </section>
                {windowSize.isMobile && criteriaData.RelevantPartsLink && <RelevantPartsLink RelevantPartsTitle={criteriaData.RelevantPartsTitle} RelevantPartsLink={criteriaData.RelevantPartsLink} />}
                {windowSize.isMobile && props.Blogs?.length > 0 && <ListPageBlogs blogs={props.Blogs} BlogTitle={props.BlogTitle} />}
                {
                    (props.ShowSEOLinks && searchData.ListingsCount > 0 && criteriaData.InternalLinkingModel?.InternalLinkGroups) ?
                        <SEOLinks
                            {...criteriaData.InternalLinkingModel}
                            LoadInternalLinksOnDemand={criteriaData.InternalLinkingModel.LoadInternalLinksOnDemand}
                            SearchURL={criteriaData.SearchBaseURL}
                            IsHostedSite={props.IsHostedSite}
                        /> : <br />
                }
                {
                    (searchData.ListingsCount > 0 && !props.IsEvents) &&
                    <div className='legal-text-container'>
                        {props.LoanApprovalEstimateModel.ShowDisclaimer && (props.CurrencyWidgetIsAvailable || props.LoanApprovalEstimateModel.ShowBanner) &&
                            <MemoLoanApprovalEstimateDisclaimer Disclaimer={props.LoanApprovalEstimateModel.Disclaimer} />}
                        {(!props.CurrencyWidgetIsAvailable && props.FinancialProviderName === 'Overdrive Commercial Funding') &&
                            <MemoLoanApprovalEstimateDisclaimer Disclaimer={translator.translate('sandhills.tradesites.mvc.overdrivelenderdisclaimer')} />}
                        {showShippingDisclaimer &&
                                <p dangerouslySetInnerHTML={{ __html: props.ShippingEstimateDisclaimer }}></p>}
                            {showVIPDisclaimer &&
                                <p>{translator.translate('sandhills.tradesites.mvc.vipvaluedisclaimer')}</p>}
                        {(props.ShowEmployeeOnlyInfo && (criteriaData.ConflictingSpecs?.length > 0 || criteriaData.ConflictingFacets?.length > 0)) && <div className='conflicting-specs-button' onClick={() => setShowConflictingSpecsModal(true)}>Conflicting Specs / Facets</div>}
                    </div>
                }
            </div>
            {(listingsSearchInProgress || criteriaSearchInProgress) && <div className='searching-background' />
            }
            {
                showEmailModal &&
                <EmailModal
                    {...criteriaData.SAMModel}
                    CurrencySelector={props.CurrencySelector}
                    toggleEmailModal={onEmailModalToggle}
                    selectedInquiry={selectedInquiry}
                    selectedContactType={selectedContactType}
                    DefaultCultureCode={props.DefaultCultureCode}
                    ShouldPushCriteoEvent={props.ShouldPushCriteoEvent}
                    ShowSocialShareButtons={showShareButtons}
                />
            }
            {
                (showBiddingAgreementModal && biddingAgreementProps) &&
                <BiddingAgreementModal
                    {...biddingAgreementProps}
                    onClose={closeBiddingAggreementModal}
                />
            }
            {
                props.LoanApprovalEstimateModel.ShowBanner && showLAE &&
                <LoanApprovalEstimateModal {...props.LoanApprovalEstimateModel} />
            }
            {
                props.ShowEmployeeOnlyInfo &&
                <ConflictingSpecsModal showModal={showConflictingSpecsModal} toggleModal={() => setShowConflictingSpecsModal(!showConflictingSpecsModal)} conflictingSpecs={criteriaData.ConflictingSpecs} conflictingFacets={criteriaData.ConflictingFacets} />
            }
            {
                showEmployeeOnlyBetaSiteToggle &&
                <BetaSiteCookieButton ToggleBetaSiteCookie={toggleBetaSiteCookie} UseBetaSiteCookie={useBetaSiteCookie} />
            }
            {
                (!currentSite.IsBetaSite && !props.UseNewListPageDesign) &&
                <FacetedSearchMobileMenu
                    vip
                    sellUrl={props.SellYourEquipmentUrl}
                    IsRY={isRY}
                    hlaUrl={searchData.HighLowAverageURL}
                    watchList={!props.IsHostedSite && props.WatchlistURL}
                    stolenUrl={props.StolenEquipmentVendorUrl}
                    filter={{
                        render: props.ShowFilter,
                        toggle: toggleMobileFilter
                    }}
                    sort={{
                        render: showMobileSort,
                        toggle: toggleMobileSort,
                        change: onSortChange,
                        options: searchData.SortOrderItems,
                        value: criteriaData.SelectedSortValue > 0 ? criteriaData.SelectedSortValue : searchData.defaultSortID
                    }}
                    flyout={{
                        render: props.UseClassicViewFlyout,
                        url: props.ClassicFlyoutModel.ClassicViewURL,
                        img: props.ClassicFlyoutModel.ClassicViewImageURL
                    }}
                    setShowDistanceModal={setShowDistanceModal}
                />
            }
            {
                (alertModalStatus.show && alertModalStatus.message) &&
                <AlertModal
                    onClose={onAlertModalClose}
                    message={alertModalStatus.message}
                    shouldRefreshPageOnClose={alertModalStatus.shouldRefreshPageOnClose}
                    containerClassName={alertModalStatus.containerClassName}
                />
            }
            {
                (currentSite.IsMobileAppContext || (windowSize.isMobile && props.UseBottomMobilePadding)) &&
                <div className='detail__mobile-padding-bottom' />
            }
            {showFTIModal &&
                <FTIModal {...props} onClose={() => setShowFTIModal(false)} />
            }
            {showRequestAQuoteModal &&
                <RequestACharterModal
                    {...criteriaData.SAMModel}
                    toggleModal={onRequestAQuoteModalToggle}
                    showRequestAQuoteModal={showRequestAQuoteModal}
                    EquipmentMessageStatus={1}
                />
            }
            {showMapViewModal &&
                <CharterMapModal toggleModal={mapViewToggle} locations={searchData.ListingLocations} listingMapData={searchData.ListingMapData} defaultZoom={searchData.DefaultZoom} defaultCenter={searchData.DefaultCenter} onLocationChange={setLocationState} currentUser={currentUser} />
            }
            {showQuickBidInfoModal &&
                <QuickBidInfoModal
                    onClose={onQuickBidInfoModalClose}                    
                />
            }
            {showQuickBidActivationModal &&
                <EnableQuickBidModal
                    onClose={() => {
                        setShowQuickBidActivationModal(false);
                    }}
                    onConfirm={() => {
                        setShowQuickBidActivationModal(false);
                        toggleQuickBidSetting(false);
                    }}
                />}
            {
                (currentSite.IsBetaSite || props.UseNewListPageDesign) &&
                <ListingControlBar
                    NoListings={searchData.ListingsCount <= 0}
                    IsWatchList={props.IsWatchList}
                    showMyBidsURL={props.ShowMyBidsURL}
                    WatchlistCount={!props.IsWatchList || props.IsWatchlistMyBids ? watchlistCount : searchData.ListingsCount}
                    hla={searchData.HighLowAverageURL && !props.IsWatchList && !isRY}
                    emailAlerts={currentSite.HasEmailAlerts}
                    IsBidCallerSite={currentSite?.Site?.IsBidcaller}
                    IsEF={props.IsEFEquipmentSearchPage}
                    IsAuctionSite={currentSite.IsAuctionSite}
                    IsHostedSite={props.IsHostedSite}
                    IsFramelessSite={currentSite.IsFramelessSite}
                    IsMobileAppContext={currentSite.IsMobileAppContext}
                    IsRY={isRY}
                    IsAV={currentSite?.Site?.IsAuctionValues}
                    ShowAV={ShowAV}
                    hlaUrl={searchData.HighLowAverageURL}
                    sellUrl={props.SellYourEquipmentUrl}
                    showFilter={showFilter()}
                    filter={{
                        render: props.ShowFilter,
                        toggle: toggleMobileFilter
                    }}
                    sort={{
                        render: showMobileSort,
                        toggle: toggleMobileSort,
                        change: onSortChange,
                        options: searchData.SortOrderItems,
                        value: criteriaData.SelectedSortValue > 0 ? criteriaData.SelectedSortValue : searchData.defaultSortID
                    }}
                    RequestACharter={currentSite?.Site?.IsCharterHub}
                    onRequestAQuoteModalToggle={onRequestAQuoteModalToggle}
                    setShowDistanceModal={setShowDistanceModal}
                    mapViewToggle={mapViewToggle}
                />
            }
            {isLoading &&
                <Backdrop
                    open={true}
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <CircularProgress
                        sx={{ willChange: 'transform', transform: 'translateZ(0)' }} />
                </Backdrop>
            }
        </>
    );
};

export default ListingsView;
global.ListingsView = ListingsView;